import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerSelect from '../components/CustomerSelect';
import RightModal from '../components/templates/RightModal';
import AddEditCustomer from '../components/customersPage/AddEditCustomer';
import AddEditSystem from '../components/systemsPage/AddEditSystem';
import AddEditAddress from '../components/customersPage/AddEditAddress';
import { setModal, selectRightModalIsOpen, selectRightModalmode, selectRightModalId, selectRightModalIsRefreshNow, selectRightModalData, selectRightModalLastClosedData, resetRefreshNow, clearLastClosedData } from '../redux/rightModalSlice';
import { formatDurationMinutes, formatDistance } from '../utils/formatUtils.js';
import { useColorPalette } from '../hooks/useColorPalette';

// Componente per le icone dinamiche
const DynamicIcon = ({ iconClass }) => (
  <i className={iconClass}></i>
);

// Componente modale di conferma
const ConfirmModal = ({ isOpen, onCancel, onConfirm, jobData, isLoading, isEditMode = false }) => {
  const palette = useColorPalette();
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 
          className="text-xl font-bold mb-4 border-b pb-2"
          style={{ color: palette.primary }}
        >
          {isEditMode ? 'Conferma modifica commessa' : 'Conferma creazione commessa'}
        </h2>
        
        <div className="space-y-3 mb-6">
          <p>Sei sicuro di voler {isEditMode ? 'modificare' : 'creare'} questa commessa?</p>
          
          <div 
            className="p-3 rounded-lg" 
            style={{ backgroundColor: palette.background, color: palette.textOnBackground }}
          >
            <h3 className="font-medium">Riepilogo dati:</h3>
            <ul className="mt-2 space-y-1 text-sm">
              <li><span className="font-medium">Tipo:</span> {jobData.jobType?.title || 'Non specificato'}</li>
              <li><span className="font-medium">Cliente:</span> {jobData.customer?.label || 'Non specificato'}</li>
              {jobData.assistanceRequest && (
                <li><span className="font-medium">Richiesta assistenza:</span> {jobData.assistanceRequest.title}</li>
              )}
              {jobData.system && (
                <li><span className="font-medium">Impianto:</span> {jobData.system.title}</li>
              )}
              {jobData.systemTypes && jobData.systemTypes.length > 0 && (
                <li>
                  <span className="font-medium">Tipi impianto:</span>
                  <span className="text-xs ml-1">
                    {jobData.systemTypes.map(t => t.typeLabel).join(', ')}
                  </span>
                </li>
              )}
              <li><span className="font-medium">Tecnici:</span> {jobData.processingData.techniciansCount}</li>
              <li><span className="font-medium">Ore previste:</span> {jobData.processingData.estimatedHours}</li>
              {jobData.processingData.dueDate && (
                <li><span className="font-medium">Scadenza:</span> {new Date(jobData.processingData.dueDate).toLocaleDateString()}</li>
              )}
              <li><span className="font-medium">Allegati:</span> {jobData.attachments.length}</li>
            </ul>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button 
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border rounded-md disabled:opacity-50"
            style={{ borderColor: palette.inputBorder }}
          >
            Annulla
          </button>
          <button 
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 rounded-md disabled:opacity-50 flex items-center"
            style={{
              backgroundColor: palette.buttonSuccessBg, 
              color: palette.buttonSuccessText,
              ':hover': { backgroundColor: palette.buttonSuccessHover }
            }}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     style={{ color: palette.buttonSuccessText }}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{isEditMode ? 'Aggiornamento' : 'Creazione'} in corso...</span>
              </>
            ) : (
              isEditMode ? 'Conferma modifiche' : 'Conferma creazione'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Array degli step del wizard con icone
const steps = [
  { title: 'Tipo commessa', icon: 'fa-briefcase' },
  { title: 'Dettaglio commessa', icon: 'fa-clipboard-list' },
  { title: 'Descrizione attività', icon: 'fa-file-alt' },
  { title: 'Dati di lavorazione', icon: 'fa-tools' }
];

// Componente per l'icona dello step
const StepIcon = ({ icon, isActive, isCompleted }) => {
  const palette = useColorPalette();
  
  const bgColor = isCompleted ? palette.stateSelected : 
                 isActive ? palette.stateFocus : 
                 palette.stateHover;
  
  const textColor = isCompleted ? palette.primary : 
                   isActive ? palette.primary : 
                   '#6B7280'; // Grigio per step inattivi
  
  const borderColor = isCompleted ? palette.primary : 
                     isActive ? palette.primary : 
                     palette.inputBorder;
  
  return (
    <div 
      className="w-14 h-14 rounded-full flex items-center justify-center border-2"
      style={{ 
        backgroundColor: bgColor,
        color: textColor,
        borderColor: borderColor
      }}
    >
      <i className={`fa-solid ${icon} text-xl`}></i>
    </div>
  );
};

// I tipi di commessa verranno presi dalle impostazioni dell'account

// Stato iniziale per il reset del form
const initialFormState = {
  jobType: null,
  customer: null,
  system: null,
  systemTypes: [],
  assistanceRequest: null,
  maintenance: null,
  selectedMaintenanceActions: [], // Array per memorizzare gli ID delle azioni di manutenzione selezionate
  description: '',
  processingData: {
    techniciansCount: 1,
    estimatedHours: 1,
    dueDate: null
  },
  attachments: []
};

// Componente personalizzato per l'anteprima di un file
const FilePreview = ({ file, onDelete, palette }) => {
  const [previewError, setPreviewError] = useState(false);
  const fileName = file.fileName || file.name || file.originalname || 'File allegato';
  const fileUrl = file.fileUrl || file.url || '';
  const mimeType = file.fileType || file.type || '';
  
  // Estrai l'estensione dal fileName o dall'URL (più affidabile per file caricati)
  let fileExtension = '';
  if (fileName.includes('.')) {
    fileExtension = fileName.split('.').pop().toLowerCase();
  } else if (fileUrl) {
    // Estrai dall'URL rimuovendo query params
    const urlPath = fileUrl.split('?')[0];
    const urlFileName = decodeURIComponent(urlPath.split('/').pop());
    if (urlFileName.includes('.')) {
      fileExtension = urlFileName.split('.').pop().toLowerCase();
    }
  }
  
  // Determina se è un'immagine basandosi sull'estensione o sul MIME type
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(fileExtension) || 
                   mimeType.startsWith('image/');
  
    
  // Determina l'icona appropriata per i documenti non-immagine
  let fileIcon = 'fa-file';
  if (['pdf'].includes(fileExtension) || mimeType.includes('pdf')) fileIcon = 'fa-file-pdf';
  else if (['doc', 'docx'].includes(fileExtension) || mimeType.includes('word')) fileIcon = 'fa-file-word';
  else if (['xls', 'xlsx'].includes(fileExtension) || mimeType.includes('excel') || mimeType.includes('spreadsheet')) fileIcon = 'fa-file-excel';
  else if (['ppt', 'pptx'].includes(fileExtension) || mimeType.includes('powerpoint') || mimeType.includes('presentation')) fileIcon = 'fa-file-powerpoint';
  else if (['zip', 'rar', 'tar', 'gz', '7z'].includes(fileExtension) || mimeType.includes('zip') || mimeType.includes('archive')) fileIcon = 'fa-file-archive';
  else if (['txt', 'rtf'].includes(fileExtension) || mimeType.includes('text')) fileIcon = 'fa-file-alt';
  
  // Gestione dell'apertura del file
  const handleOpen = () => {
    if (fileUrl) {
      console.log(`Apertura file: ${fileUrl}`);
      window.open(fileUrl, '_blank');
    }
  };
  
  return (
    <div className="flex flex-col border rounded-lg overflow-hidden bg-white" style={{ 
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)' 
    }}>
      {/* Anteprima file */}
      <div 
        className="relative h-32 flex items-center justify-center cursor-pointer"
        style={{
          backgroundColor: isImage ? '#f3f4f6' : (palette.neutralBg || '#f3f4f6') 
        }}
        onClick={handleOpen}
      >
        {/* Badge per file esistente */}
        {file.isExistingFile && (
          <div className="absolute top-1 left-1 z-10">
            <span className="text-xs rounded-md px-1.5 py-0.5" style={{ 
              backgroundColor: palette.successBg || '#d1fae5',
              color: palette.success || '#059669'
            }}>
              <i className="fa-solid fa-check-circle mr-1"></i>
              Salvato
            </span>
          </div>
        )}
        
        {isImage && fileUrl && !previewError ? (
          // Mostra anteprima per le immagini
          <>
            <img 
              src={fileUrl}
              alt={fileName}
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', display: 'block' }}
              onError={() => setPreviewError(true)}
            />
          </>
        ) : (
          // Mostra icona per documenti o immagini con errore
          <div className="flex flex-col items-center justify-center">
            <i className={`fa-solid ${fileIcon} text-4xl mb-2`} style={{ 
              color: palette.primary || '#3182ce' 
            }}></i>
            {previewError && (
              <span className="text-xs text-gray-500">Anteprima non disponibile</span>
            )}
          </div>
        )}
        
        {/* Overlay hover con icona per aprire */}
        {fileUrl && (
          <div className="absolute inset-0 flex items-center justify-center transition-all duration-200 hover:bg-black hover:bg-opacity-20 pointer-events-none">
            <i className="fa-solid fa-eye text-white opacity-0 text-xl"></i>
          </div>
        )}
      </div>
      
      {/* Footer con nome file e pulsante elimina */}
      <div className="p-2 border-t" style={{backgroundColor: palette.neutralBg || '#f9fafb'}}>
        {/* Nome file */}
        <p className="text-xs font-medium truncate" title={fileName}>
          {fileName}
        </p>
        {/* Riga con tipo file e pulsante elimina */}
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs" style={{ color: palette.neutralText || '#9ca3af' }}>
            {mimeType ? (mimeType.split('/')[1] || mimeType) : fileExtension || 'file'}
          </span>
          <button
            type="button"
            className="px-2 py-1 rounded hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: palette.danger || '#e53e3e',
              color: '#ffffff',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onDelete) onDelete();
            }}
            title="Rimuovi file"
          >
            <i className="fa-solid fa-trash-alt text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );
};  

const CreateJob = ({ isModal = false, jobId: propJobId = null, onClose = null }) => {
  // Recupera l'ID dalla URL se presente
  const { id: urlJobId } = useParams();
  // Usa l'ID fornito come prop o quello dell'URL
  const jobId = propJobId || urlJobId || null;
  const isEditMode = !!jobId;
  
  // Ottieni la palette di colori dell'account
  const palette = useColorPalette();
  
  // Debug
  useEffect(() => {
    if (isEditMode) {
      console.log(`Modalità modifica commessa attivata. ID: ${jobId}`);
    }
  }, [isEditMode, jobId]);
  
  // Stato centrale del wizard
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormState);
  
  // Stato locale per gestire gli allegati
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Redux dispatch
  const dispatch = useDispatch();
  
  // Selettori RightModal per gestire la creazione cliente e impianti
  const rightModalIsOpen = useSelector(selectRightModalIsOpen);
  const rightModalMode = useSelector(selectRightModalmode);
  const rightModalCustomerId = useSelector(selectRightModalId);
  const rightModalIsRefreshNow = useSelector(selectRightModalIsRefreshNow);
  const rightModalData = useSelector(selectRightModalData);
  const rightModalLastClosedData = useSelector(selectRightModalLastClosedData);
  
  // Stato per tracciare se stavamo creando un cliente o un impianto
  const [wasCreatingCustomer, setWasCreatingCustomer] = useState(false);
  const [wasCreatingSystem, setWasCreatingSystem] = useState(false);
  
  // Stati di loading
  const [loading, setLoading] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);
  const [customerSystems, setCustomerSystems] = useState([]);
  const [customerSystemsLoading, setCustomerSystemsLoading] = useState(false);
  const [assistanceRequests, setAssistanceRequests] = useState([]);
  const [assistanceRequestsLoading, setAssistanceRequestsLoading] = useState(false);
  const [assistanceSearchTerm, setAssistanceSearchTerm] = useState('');
  const [maintenances, setMaintenances] = useState([]);
  const [maintenancesLoading, setMaintenancesLoading] = useState(false);
  const [maintenanceSearchTerm, setMaintenanceSearchTerm] = useState('');
  
  // Token di autenticazione
  const token = localStorage.getItem('installamelo-auth-token');
  const accountData = useSelector(state => state.account?.data) || {};
  const accountId = accountData._id;
  const navigate = useNavigate();

  // Recupera le impostazioni dell'account, inclusi gli stati delle commesse e i tipi di commessa
  const accountSettings = accountData.settings || {};
  const jobStatuses = accountSettings.jobStatus || [];
  const jobTypesSettings = accountSettings.jobTypes || [];

  // Debug delle impostazioni non appena disponibili
  useEffect(() => {
    if (accountSettings && jobTypesSettings) {
      console.log('Tipi commessa caricati:', jobTypesSettings);
    }
  }, [accountSettings, jobTypesSettings]);
  
  // Funzione per recuperare i dettagli completi di una manutenzione
  const fetchMaintenanceDetails = async (maintenanceId) => {
    if (!maintenanceId) return null;
    try {
      console.log('Recupero dettagli completi della manutenzione ID:', maintenanceId);
      const response = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances?maintenanceId=${maintenanceId}`,
        { headers: { 'auth-token': token } }
      );
      
      if (response.data && response.status === 200) {
        const maintenanceData = response.data.results || response.data;
        const maintenance = Array.isArray(maintenanceData) ? maintenanceData[0] : maintenanceData;
        
        if (maintenance) {
          console.log('Manutenzione recuperata:', maintenance.title || maintenance.code);
          return maintenance;
        }
      }
      
      console.warn('Nessun dettaglio trovato per la manutenzione ID:', maintenanceId);
      return null;
    } catch (error) {
      console.error('Errore nel recupero dei dettagli della manutenzione:', error);
      toast.error('Errore nel caricamento dei dettagli della manutenzione');
      return null;
    }
  };

  // Effetto per caricare i dati della commessa quando viene fornito un jobId (modalità modifica)
  useEffect(() => {
    const loadJobData = async () => {
      if (!jobId || !token || !accountId) return;
      
      // Reset dello stato e dei messaggi di errore prima del caricamento
      setLoading(true);
      toast.info(`Caricamento dati della commessa in corso...`);
      
      try {
        console.log(`Caricamento dati commessa ID: ${jobId}`);
        // Chiamata API per ottenere i dati della commessa
        const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/jobs`, {
          headers: { 'auth-token': token },
          params: { jobId }
        });
        
        // Estrai i dati della risposta (gestisci sia response.data che response.data.ret)
        const jobData = response.data && response.data._id ? response.data : 
                       response.data && response.data.ret && response.data.ret._id ? response.data.ret : null;
        
        if (!jobData) {
          throw new Error('Dati commessa non trovati nella risposta API');
        }
        
        console.log('=== DATI COMMESSA CARICATI ===', jobData);
        
        // Log dettagliati se è una commessa di manutenzione
        if (jobData.jobTypeMaintenance) {
          console.log('=== DETTAGLIO COMMESSA MANUTENZIONE ===');
          console.log('ID commessa:', jobData._id);
          console.log('Codice commessa:', jobData.code);
          console.log('Cliente:', { id: jobData.customerId, label: jobData.customerLabel });
          console.log('Data programmata:', jobData.scheduledDate);
          console.log('Stato commessa:', { id: jobData.statusId, label: jobData.statusLabel });
          console.log('Descrizione:', jobData.description || 'Non specificata');
          console.log('Note:', jobData.notes || 'Non specificate');
          
          // Dettagli manutenzione
          console.log('ID manutenzione:', jobData.maintenanceId || 'Non specificato');
          console.log('Codice manutenzione:', jobData.maintenanceCode || 'Non specificato');
          
          // Log dettagliato delle azioni di manutenzione
          if (Array.isArray(jobData.maintenanceActions) && jobData.maintenanceActions.length > 0) {
            console.log('Azioni di manutenzione associate:', jobData.maintenanceActions.length);
            jobData.maintenanceActions.forEach((action, index) => {
              console.log(`Azione #${index + 1}:`, {
                id: action.actionId,
                titolo: action.title,
                note: action.notes || 'Nessuna nota',
                periodo: `${action.month}/${action.year}`,
                stato: action.statusLabel || 'Stato non definito'
              });
            });
          } else {
            console.log('Nessuna azione di manutenzione associata');
          }
          
          // Sistema e tipi
          console.log('Sistema:', { id: jobData.systemId, label: jobData.systemLabel });
          if (Array.isArray(jobData.systemTypeList) && jobData.systemTypeList.length > 0) {
            console.log('Tipi di sistema associati:', jobData.systemTypeList);
          } else {
            console.log('Nessun tipo di sistema associato');
          }
        }
        
        // Se la commessa è di tipo assistenza, carica i dettagli completi della richiesta
        let assistanceRequestDetails = null;
        if (jobData.assistanceRequestId) {
          try {
            console.log('Caricamento dettagli richiesta assistenza:', jobData.assistanceRequestId);
            
            // Utilizziamo l'endpoint specifico per caricare solo l'assistenza associata
            // passando sia accountId che assistanceRequestId come richiesto
            const assistanceResponse = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/assistanceRequests`, {
              params: {
                accountId: accountId,
                assistanceRequestId: jobData.assistanceRequestId
              },
              headers: { 'auth-token': token }
            });
            
            assistanceRequestDetails = assistanceResponse.data.data || assistanceResponse.data.ret || assistanceResponse.data;
            console.log('Dettagli richiesta assistenza caricati:', assistanceRequestDetails);
          } catch (assistanceError) {
            console.error('Errore nel caricamento dei dettagli della richiesta di assistenza:', assistanceError);
            toast.warning('Non è stato possibile caricare tutti i dettagli della richiesta di assistenza');
          }
        }
        
        // Funzione per recuperare i dettagli di una richiesta di assistenza specifica
        const fetchAssistanceRequestDetails = async (assistanceRequestId) => {
          try {
            const response = await axios.get(
              `${import.meta.env.VITE_API_BASE_URL || ''}/assistance-requests?id=${assistanceRequestId}`,
              {
                headers: { 'auth-token': token }
              }
            );
            if (response.data && response.data.results && response.data.results.length > 0) {
              console.log('Dettagli della richiesta di assistenza recuperati:', response.data.results[0]);
              return response.data.results[0];
            } else {
              console.warn('Nessun dettaglio trovato per la richiesta di assistenza ID:', assistanceRequestId);
              return null;
            }
          } catch (error) {
            console.error('Errore nel recupero dei dettagli della richiesta di assistenza:', error);
            toast.error('Errore nel caricamento dei dettagli della richiesta di assistenza');
            return null;
          }
        };
        
        // Se è presente un ID di manutenzione, carica i dettagli completi della manutenzione
        let maintenanceDetails = null;
        let maintenanceId = jobData.maintenanceId; // Salviamo esplicitamente l'ID
        
        if (maintenanceId) {
          console.log('=== INIZIO CARICAMENTO DETTAGLI MANUTENZIONE ===');
          console.log('ID manutenzione da caricare:', maintenanceId);
          
          try {
            // Chiamata diretta e semplificata all'API
            const maintenanceResponse = await axios.get(
              `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances?maintenanceId=${maintenanceId}`,
              { headers: { 'auth-token': token } }
            );
            
            console.log('Risposta API manutenzione:', maintenanceResponse.status);
            console.log('Dati ottenuti:', maintenanceResponse.data);
            
            // Estrai i dati dalla risposta (gestisci sia results che risposta diretta)
            const maintenanceData = maintenanceResponse.data.results || maintenanceResponse.data;
            
            // Normalizza il risultato (gestisci sia oggetto che array)
            if (maintenanceData) {
              if (Array.isArray(maintenanceData)) {
                maintenanceDetails = maintenanceData[0];
                console.log('Manutenzione estratta da array, ID:', maintenanceDetails._id);
              } else if (typeof maintenanceData === 'object') {
                maintenanceDetails = maintenanceData;
                console.log('Manutenzione come oggetto singolo, ID:', maintenanceDetails._id);
              }
              
              // Verifica contenuto
              if (!maintenanceDetails || !maintenanceDetails._id) {
                console.error('ERRORE: Manutenzione caricata non valida!');
                console.log('Dettagli ricevuti:', maintenanceDetails);
              } else {
                console.log('Manutenzione caricata con successo:');
                console.log(' - ID:', maintenanceDetails._id);
                console.log(' - Titolo:', maintenanceDetails.title || maintenanceDetails.code);
                console.log(' - Cliente:', maintenanceDetails.customerLabel || 'N/D');
              }
              
              // Normalizza le azioni (sempre)
              if (maintenanceDetails && Array.isArray(maintenanceDetails.actions)) {
                console.log(`Manutenzione contiene ${maintenanceDetails.actions.length} azioni:`);
                
                // Log dettagliato delle azioni
                maintenanceDetails.actions.forEach((action, idx) => {
                  console.log(` - Azione ${idx + 1}: ID=${action._id || action.id}, Titolo=${action.title}`);
                });
                
                // Normalizzazione completa delle azioni
                maintenanceDetails.actions = maintenanceDetails.actions.map(action => ({
                  ...action,
                  _id: action._id || action.id,
                  id: action.id || action._id,
                  actionId: action._id || action.id // Aggiungiamo actionId per compatibilità UI
                }));
              } else {
                maintenanceDetails.actions = [];
                console.log('La manutenzione non contiene azioni');
              }
              
              console.log('=== FINE CARICAMENTO DETTAGLI MANUTENZIONE ===');
            } else {
              console.error('ERRORE: Dati manutenzione non trovati nella risposta API');
              console.log('Risposta completa:', maintenanceResponse.data);
            }
          } catch (error) {
            console.error('Errore critico nel caricamento manutenzione:', error.message);
            toast.error('Errore nel caricamento dei dettagli della manutenzione');
          }
          
          // Log dei campi principali (solo se abbiamo caricato con successo)
          if (maintenanceDetails) {
            console.log('Campi principali della manutenzione:');
            console.log(' - customerLabel:', maintenanceDetails.customerLabel || 'N/D');
            console.log(' - systemLabel:', maintenanceDetails.systemLabel || 'N/D');
            console.log(' - systemAddressLabel:', maintenanceDetails.systemAddressLabel || 'N/D');
          }
        }
        
        // Trova il tipo di commessa corrispondente nelle impostazioni
        let matchingJobType = jobTypesSettings.find(type => type.id === jobData.jobTypeId);
        
        // Se non troviamo una corrispondenza esatta, proviamo con altre proprietà
        if (!matchingJobType) {
          matchingJobType = jobTypesSettings.find(type => {
            return (
              // Prova matching per label o titolo
              (type.label && jobData.jobTypeLabel && type.label.toLowerCase() === jobData.jobTypeLabel.toLowerCase()) ||
              // Se c'è un flag di tipo nella commessa, cerca anche quello
              (jobData.jobTypeAssistance && determineJobTypeFlag(type, 'assistance')) ||
              (jobData.jobTypeMaintenance && determineJobTypeFlag(type, 'maintenance')) ||
              (jobData.jobTypeSale && determineJobTypeFlag(type, 'sales', 'sale')) ||
              (jobData.jobTypeGeneric && determineJobTypeFlag(type, 'generic'))
            );
          });
        }
        
        if (!matchingJobType && jobData.jobTypeLabel) {
          // Se ancora non troviamo un match ma abbiamo un'etichetta, creiamo un tipo minimal
          console.warn(`Tipo commessa non trovato nelle impostazioni: ${jobData.jobTypeLabel}. Usando dati minimi disponibili.`);
          matchingJobType = {
            id: jobData.jobTypeId || 'unknown',
            title: jobData.jobTypeLabel || 'Tipo commessa',
            label: jobData.jobTypeLabel || 'Tipo commessa',
            // Imposta i flag dal jobData
            jobTypeAssistance: jobData.jobTypeAssistance || false,
            jobTypeMaintenance: jobData.jobTypeMaintenance || false,
            jobTypeSale: jobData.jobTypeSale || false,
            jobTypeGeneric: jobData.jobTypeGeneric || false
          };
        }
        
        // Gestisci i dati geografici in modo coerente con la soluzione GeoData
        let geoData = undefined;
        if (jobData.geoData) {
          // Se la commessa ha già dei dati geografici, usali
          geoData = {
            type: jobData.geoData.type,
            coordinates: jobData.geoData.coordinates
          };
        } else if (jobData.assistanceRequestId && jobData.outboundCoordinateLat && jobData.outboundCoordinateLng) {
          // Crea geoData dalle coordinate outbound se disponibili
          geoData = {
            type: 'Point',
            coordinates: [parseFloat(jobData.outboundCoordinateLng), parseFloat(jobData.outboundCoordinateLat)]
          };
        } else if (accountSettings?.headquarterCoordinates) {
          // Usa le coordinate della sede centrale come fallback
          const hqCoords = accountSettings.headquarterCoordinates;
          geoData = {
            type: 'Point',
            coordinates: [parseFloat(hqCoords.lng), parseFloat(hqCoords.lat)]
          };
        }
        
        // Verifica se sono presenti azioni di manutenzione nella commessa
        let selectedMaintenanceActions = [];
        if (Array.isArray(jobData.maintenanceActions) && jobData.maintenanceActions.length > 0) {
          console.log('Trovate azioni di manutenzione associate alla commessa:', jobData.maintenanceActions);
          
          // Estrai gli ID delle azioni
          selectedMaintenanceActions = jobData.maintenanceActions.map(action => action.actionId || action._id);
          console.log('ID delle azioni di manutenzione estratti dalla commessa:', selectedMaintenanceActions);
        } else if (maintenanceDetails && Array.isArray(maintenanceDetails.actions)) {
          // Se non ci sono azioni nella commessa ma ci sono nei dettagli della manutenzione
          // In modalità modifica, consideriamo tutte le azioni della manutenzione come selezionate
          console.log('Utilizzando le azioni dai dettagli della manutenzione:', maintenanceDetails.actions);
          selectedMaintenanceActions = maintenanceDetails.actions
            .filter(action => action && (action._id || action.id))
            .map(action => action._id || action.id);
          console.log('ID delle azioni estratti dai dettagli manutenzione:', selectedMaintenanceActions);
        } else {
          console.log('Nessuna azione di manutenzione trovata né nella commessa né nei dettagli della manutenzione');
        }
        
        // Log dettagliato per aiutare il debug
        if (jobId && maintenanceDetails) {
          console.log('Dettaglio caricamento azioni in modalità modifica:');
          console.log('- maintenanceDetails._id:', maintenanceDetails._id);
          console.log('- Numero di azioni nella manutenzione:', maintenanceDetails.actions?.length || 0);
          console.log('- Azioni selezionate:', selectedMaintenanceActions);
        }
        
        // Popola il form con i dati della commessa
        const updatedFormData = {
          jobType: matchingJobType ? {
            id: matchingJobType.id,
            title: matchingJobType.label || matchingJobType.title || jobData.jobTypeLabel,
            customFields: matchingJobType.customFields || [],
            // Aggiungi i flag di tipo
            assistance: !!jobData.jobTypeAssistance || determineJobTypeFlag(matchingJobType, 'assistance'),
            maintenance: !!jobData.jobTypeMaintenance || determineJobTypeFlag(matchingJobType, 'maintenance'),
            sale: !!jobData.jobTypeSale || determineJobTypeFlag(matchingJobType, 'sales', 'sale'),
            generic: !!jobData.jobTypeGeneric || determineJobTypeFlag(matchingJobType, 'generic')
          } : null,
          customer: jobData.customerId ? {
            id: jobData.customerId,
            label: jobData.customerLabel || 'Cliente',
            value: jobData.customerId
          } : null,
          system: null, // Inizialmente null, caricato dopo il caricamento degli impianti
          systemTypes: [], // Inizialmente vuoto, caricato dopo la selezione dell'impianto
          // Usa i dettagli completi dell'assistenza se disponibili, altrimenti usa i dati minimi
          assistanceRequest: jobData.assistanceRequestId ? 
            // Se abbiamo caricato i dettagli completi della richiesta, li usiamo
            (assistanceRequestDetails ? assistanceRequestDetails : {
              _id: jobData.assistanceRequestId,
              id: jobData.assistanceRequestId,
              code: jobData.assistanceRequestCode || null,
              title: jobData.assistanceRequestLabel || jobData.assistanceRequestCode || 'Richiesta di assistenza',
              value: jobData.assistanceRequestId,
              customerLabel: jobData.customerLabel || null
            })
          : null,
          // Salvare l'ID della manutenzione esplicitamente come campo a parte
          maintenanceId: jobData.maintenanceId,
          
          // Gestione dettagliata dell'oggetto manutenzione
          maintenance: jobData.maintenanceId && maintenanceDetails ? {
            ...maintenanceDetails,
            id: maintenanceDetails._id,
            _id: maintenanceDetails._id,  // Garantiamo presenza di entrambi id e _id
            title: maintenanceDetails.code || maintenanceDetails.title || jobData.maintenanceLabel || 'Manutenzione',
            value: maintenanceDetails._id,
            code: maintenanceDetails.code,
            
            // IMPORTANTE: Aggiungiamo flag speciali per simulare selezione diretta nella UI
            selected: true,  
            isSelected: true,
            
            // Garantiamo che i campi critici siano sempre presenti
            customerLabel: maintenanceDetails.customerLabel,
            systemLabel: maintenanceDetails.systemLabel,
            systemAddressLabel: maintenanceDetails.systemAddressLabel,
            
            // Normalizzazione completa e profonda delle azioni
            actions: Array.isArray(maintenanceDetails.actions) ? maintenanceDetails.actions.map(action => ({
              ...action,
              _id: action._id || action.id, 
              id: action.id || action._id,
              actionId: action._id || action.id, // Campo speciale per compatibilità UI
              title: action.title || action.name || 'Azione' // Garantiamo un titolo
            })) : []
          } : null,
          // Azioni di manutenzione preselezionate
          selectedMaintenanceActions: selectedMaintenanceActions,
          description: jobData.description || jobData.notes || '',
          processingData: {
            techniciansCount: jobData.employeesNumber || jobData.techniciansCount || 1,
            estimatedHours: jobData.expectedHours || jobData.estimatedHours || 1,
            dueDate: jobData.dueDate ? new Date(jobData.dueDate).toISOString().split('T')[0] : 
                     jobData.scheduledDate ? new Date(jobData.scheduledDate).toISOString().split('T')[0] : ''
          },
          // Normalizzare gli allegati esistenti per garantire che tutti abbiano un nome visualizzabile
          attachments: (jobData.uploads || jobData.attachments || []).map(attachment => {
            // Estrai l'URL del file da vari campi possibili
            const url = attachment.fileUrl || attachment.urlFile || attachment.url || attachment.path || '';
            // Estrai il nome del file dall'URL se non presente
            const extractedName = url ? decodeURIComponent(url.split('/').pop().split('?')[0]) : 'File allegato';
            
            return {
              ...attachment,
              // Assicuriamo che ci sia sempre un nome per il file
              name: attachment.name || attachment.fileName || attachment.filename || attachment.originalname || extractedName,
              // URL del file per anteprima e apertura
              fileUrl: url,
              // Tipo del file (estrai dall'estensione se non presente)
              fileType: attachment.fileType || attachment.type || attachment.mimeType || '',
              // Flag per identificare che questo è un file già esistente sul server
              isExistingFile: true,
              // Salviamo l'ID originale per riferimento al backend
              fileId: attachment._id || attachment.id || attachment.fileId
            };
          }),
          // Dati aggiuntivi da salvare per la modifica
          _id: jobData._id,
          code: jobData.code,
          creationDate: jobData.creationDate,
          statusId: jobData.statusId,
          statusLabel: jobData.statusLabel,
          geoData: geoData,
          // Salviamo per dopo i dati di systemId e systemTypeList
          _systemIdToSelect: jobData.systemId,
          _systemLabelToSelect: jobData.systemLabel,
          _systemTypeListToSelect: Array.isArray(jobData.systemTypeList) ? jobData.systemTypeList : []
        };
        
        setFormData(updatedFormData);
        
        // Verifica esplicita che la manutenzione sia stata caricata correttamente
        // Verifica dettagliata dell'oggetto manutenzione dopo la costruzione del form data
        if (jobData.maintenanceId && updatedFormData.maintenance) {
          console.log('VERIFICA ESPLICITA MANUTENZIONE:', {
            'ID manutenzione nel job': jobData.maintenanceId,
            'Oggetto manutenzione presente': !!updatedFormData.maintenance,
            'ID nell\'oggetto': updatedFormData.maintenance?._id || updatedFormData.maintenance?.id || 'Non presente',
            'customerLabel presente': !!updatedFormData.maintenance?.customerLabel,
            'systemLabel presente': !!updatedFormData.maintenance?.systemLabel,
            'Numero azioni': updatedFormData.maintenance?.actions?.length || 0,
            'Azioni selezionate': updatedFormData.selectedMaintenanceActions?.length || 0
          });
          
          // Forza la selezione e il riconoscimento della manutenzione
          if (updatedFormData.maintenance && !updatedFormData.maintenance.isSelected) {
            const enhancedMaintenance = {
              ...updatedFormData.maintenance,
              selected: true,
              isSelected: true,
              // Garantisci sempre che questi campi siano presenti
              customerLabel: updatedFormData.maintenance.customerLabel,
              systemLabel: updatedFormData.maintenance.systemLabel,
              systemAddressLabel: updatedFormData.maintenance.systemAddressLabel
            };
            
            console.log('MANUTENZIONE FORZATAMENTE SELEZIONATA:', enhancedMaintenance);
            // Aggiorna il form data con la manutenzione migliorata
            setFormData(prev => ({...prev, maintenance: enhancedMaintenance}));
          }
        }
        
        // Aggiorna lo stato del form e indica che è stato caricato
        setFormData(updatedFormData);
        setLoading(false);
        setFormLoaded(true);
        toast.success(`Dati della commessa caricati con successo`);
        
        console.log('=== RIEPILOGO DATI FORM DOPO AGGIORNAMENTO ===');
        console.log('- jobType:', updatedFormData.jobType?.title);
        console.log('- customer:', updatedFormData.customer?.label);
        console.log('- maintenanceId (campo separato):', updatedFormData.maintenanceId);
        console.log('- Manutenzione presente?', !!updatedFormData.maintenance);
        
        // Debug dettagliato oggetto manutenzione
        if (updatedFormData.maintenance) {
          console.log('>> DETTAGLI MANUTENZIONE');
          console.log('  - ID:', updatedFormData.maintenance._id);
          console.log('  - Titolo:', updatedFormData.maintenance.title);
          console.log('  - Cliente:', updatedFormData.maintenance.customerLabel);
          console.log('  - Impianto:', updatedFormData.maintenance.systemLabel);
          console.log('  - Azioni:', updatedFormData.maintenance.actions?.length || 0);
          console.log('  - Flag selected:', updatedFormData.maintenance.selected);
          console.log('  - Flag isSelected:', updatedFormData.maintenance.isSelected);
        } else {
          console.warn('>> MANUTENZIONE MANCANTE ANCHE DOPO SET');
        }
        
        console.log('- Azioni selezionate:', updatedFormData.selectedMaintenanceActions?.length || 0);
        if (updatedFormData.selectedMaintenanceActions?.length > 0) {
          console.log('>> IDs AZIONI SELEZIONATE:', updatedFormData.selectedMaintenanceActions);
        }
        
        // Se il cliente è selezionato, carica i suoi impianti
        if (updatedFormData.customer) {
          console.log(`Caricamento impianti per cliente ID: ${updatedFormData.customer.id}`);
          fetchCustomerSystems(updatedFormData.customer.id)
            .then((systemsData) => {
              // Dopo il caricamento degli impianti, cerchiamo l'impianto da selezionare
              if (updatedFormData._systemIdToSelect) {
                console.log('Cercando impianto con ID:', updatedFormData._systemIdToSelect);
                console.log('Lista impianti disponibili (dal risultato diretta):', systemsData.map(sys => sys._id));
                
                // IMPORTANTE: usiamo systemsData restituito direttamente dalla Promise, non customerSystems
                const systemToSelect = systemsData.find(sys => sys._id === updatedFormData._systemIdToSelect);
                
                if (systemToSelect) {
                  console.log('Impianto trovato e selezionato:', systemToSelect);
                  // Aggiorniamo il form con l'impianto selezionato
                  updateFormData('system', {
                    id: systemToSelect._id,
                    _id: systemToSelect._id,
                    title: systemToSelect.title || systemToSelect.name || updatedFormData._systemLabelToSelect || 'Impianto',
                    value: systemToSelect._id,
                    customerAddressLabel: systemToSelect.customerAddressLabel || ''
                  });
                  
                  // Selezioniamo anche i tipi di impianto
                  if (updatedFormData._systemTypeListToSelect && updatedFormData._systemTypeListToSelect.length > 0) {
                    console.log('Tipi impianto da selezionare:', updatedFormData._systemTypeListToSelect);
                    updateFormData('systemTypes', updatedFormData._systemTypeListToSelect);
                  }
                  
                  // Selezioniamo anche i tipi di impianto dalla lista dell'impianto se presente
                  if (systemToSelect.typesList && systemToSelect.typesList.length > 0) {
                    console.log('Tipi impianto trovati nell\'impianto:', systemToSelect.typesList);
                    const typesFromSystem = systemToSelect.typesList.map(type => ({
                      typeId: type.typeId,
                      typeLabel: type.typeLabel,
                      testedDate: type.testedDate,
                      expirationWarrantyDate: type.expirationWarrantyDate
                    }));
                    updateFormData('systemTypes', typesFromSystem);
                  }
                } else {
                  console.warn(`Impianto con ID ${updatedFormData._systemIdToSelect} non trovato nella lista caricata`);
                  console.log('IDs disponibili:', systemsData.map(sys => ({ _id: sys._id, title: sys.title })));
                  
                  // Tenta un'ultima verifica con confronto flessibile per eventuali ID in formati diversi
                  const normalizedTargetId = updatedFormData._systemIdToSelect.toString().trim();
                  const systemByFlexibleMatch = systemsData.find(sys => {
                    const normalizedSystemId = (sys._id || '').toString().trim();
                    return normalizedSystemId === normalizedTargetId;
                  });
                  
                  if (systemByFlexibleMatch) {
                    console.log('Impianto trovato con confronto flessibile:', systemByFlexibleMatch);
                    updateFormData('system', {
                      id: systemByFlexibleMatch._id,
                      _id: systemByFlexibleMatch._id,
                      title: systemByFlexibleMatch.title || systemByFlexibleMatch.name || updatedFormData._systemLabelToSelect || 'Impianto',
                      value: systemByFlexibleMatch._id,
                      customerAddressLabel: systemByFlexibleMatch.customerAddressLabel || ''
                    });
                  }
                }
              }
            })
            .catch(err => {
              console.error('Errore nel selezionare l\'impianto dopo il caricamento:', err);
            });
        }
        
        // In modalità modifica, iniziamo sempre dallo step 0 (tipo commessa)
        // Questo permette all'utente di vedere chiaramente il tipo di commessa selezionato
        // prima di procedere con gli altri step
        if (updatedFormData.jobType) {
          console.log('Impostazione step iniziale in modalità modifica: 0 (tipo commessa)');
          setCurrentStep(0);
          
          // Nota: abbiamo rimosso la logica di avanzamento automatico per mostrare sempre
          // lo step del tipo commessa all'apertura di una commessa in modifica, come richiesto
        }
        
      } catch (error) {
        console.error('Errore nel caricamento dei dati della commessa:', error);
        toast.error(`Errore nel caricamento dei dati della commessa: ${error.message || 'Errore sconosciuto'}`);
      } finally {
        setLoading(false);
      }
    };
    
    loadJobData();
  }, [jobId, token, accountId, jobTypesSettings]);
  
  // Stato per gestire il caricamento durante l'invio e la modale di conferma
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Funzioni di navigazione
  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };
  
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };
  
  // Funzione per il caricamento degli allegati
  const uploadAttachments = async () => {
    if (!formData.attachments || formData.attachments.length === 0) {
      console.log('Nessun allegato da caricare');
      return { success: true, uploads: [] };
    }
    
    try {
      // Array per contenere gli URL dei file caricati e dei file esistenti
      const uploads = [];
      
      // Contatori per log
      let existingFilesCount = 0;
      let newFilesCount = 0;
      
      // Prima gestisci i file già esistenti
      for (let i = 0; i < formData.attachments.length; i++) {
        const attachment = formData.attachments[i];
        
        // Se è un file già esistente (già caricato in precedenza), mantienilo senza ricaricarlo
        if (attachment.isExistingFile) {
          existingFilesCount++;
          console.log(`File ${i+1}/${formData.attachments.length}: "${attachment.name}" è già esistente, preservato`);
          
          // Aggiungi direttamente il file esistente all'array uploads
          const existingFileInfo = {
            url: attachment.url || attachment.fileUrl,
            fileType: attachment.type || attachment.fileType || '',
            fileName: attachment.name || attachment.fileName || attachment.originalname || '',
            // Mantieni l'id originale per il backend
            fileId: attachment.fileId || attachment._id || attachment.id
          };
          
          uploads.push(existingFileInfo);
          continue; // Passa al prossimo file
        }
        
        // A questo punto sappiamo che è un nuovo file da caricare
        newFilesCount++;
        console.log(`Caricamento nuovo file ${i+1}/${formData.attachments.length}: ${attachment.name}`);
        
        // Verifica che il file effettivo sia presente
        if (!attachment.file) {
          console.warn(`Il file ${attachment.name} non ha il campo 'file', saltato`);
          continue;
        }
        
        const fileData = new FormData();
        fileData.append('accountId', accountId);
        fileData.append('file', attachment.file);
        fileData.append('path', 'commesse');
        fileData.append('uploadTitle', `Commessa ${formData.jobType?.title || ''} - ${attachment.name}`);
        
        try {
          // Chiamata API per caricare il file usando l'endpoint corretto
          const response = await axios.post(
            `${import.meta.env.VITE_API_BASE_URL || ''}/files/upload`,
            fileData,
            {
              headers: {
                'auth-token': token,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          console.log(`Risposta API per file ${i+1}:`, response.data);
          
          // Verifica la risposta e salva sia l'URL che il tipo di file e il nome
          if (response.data && response.data.fileUrl) {
            const uploadInfo = {
              url: response.data.fileUrl,
              fileType: attachment.type || '',
              fileName: attachment.name || ''
            };
            uploads.push(uploadInfo);
            console.log(`File ${i+1} caricato con successo:`, uploadInfo);
          } else if (response.data && response.data.url) {
            const uploadInfo = {
              url: response.data.url,
              fileType: attachment.type || '',
              fileName: attachment.name || ''
            };
            uploads.push(uploadInfo);
            console.log(`File ${i+1} caricato con successo (formato alternativo):`, uploadInfo);
          } else {
            console.warn(`File ${i+1} - Risposta upload non contiene URL:`, response.data);
            // Se la risposta contiene qualsiasi campo che potrebbe essere un URL, proviamo a usarlo
            for (const key in response.data) {
              if (typeof response.data[key] === 'string' && 
                  (response.data[key].startsWith('http://') || response.data[key].startsWith('https://'))) {
                console.log(`Trovato possibile URL nel campo ${key}:`, response.data[key]);
                uploads.push({
                  url: response.data[key],
                  fileType: attachment.type || '',
                  fileName: attachment.name || ''
                });
                break;
              }
            }
          }
        } catch (uploadError) {
          console.error(`Errore durante il caricamento del file ${i+1}:`, uploadError);
          toast.error(`Errore durante il caricamento del file ${attachment.name}: ${uploadError.message}`);
        }
      }
      
      console.log(`Upload completati: ${uploads.length}/${formData.attachments.length}`);
      console.log('Dettaglio uploads:', JSON.stringify(uploads, null, 2));
      
      if (uploads.length === 0 && formData.attachments.length > 0) {
        console.warn('ATTENZIONE: Nessun file è stato caricato con successo!');
      }
      
      return { success: true, uploads };
    } catch (error) {
      console.error('Errore generale durante il caricamento dei file:', error);
      toast.error(`Errore generale durante il caricamento: ${error.message}`);
      return { success: false, error: error.message };
    }
  };
  
  // Mostra il modale di conferma prima di creare la commessa
  const handleCreateJob = () => {
    // Verifica validazione dell'ultimo step
    if (!validateCurrentStep()) {
      return;
    }
    
    // Mostra il modale di conferma
    setShowConfirmModal(true);
  };
  
  // Modifica la funzione di navigazione per gestire la chiusura della modale
  const handleAfterSave = () => {
    if (isModal) {
      onClose?.(); // Chiude la modale
    } else {
      navigate('/jobs'); // Naviga alla lista commesse
    }
  };
  
  // Funzione per creare o aggiornare la commessa dopo la conferma
  const confirmCreateJob = async () => {
    setSubmitting(true);
    
    try {
      // 1. Prima carica gli allegati
      const uploadsResult = await uploadAttachments();
      
      if (!uploadsResult.success) {
        toast.error(`Errore nel caricamento degli allegati: ${uploadsResult.error}`);
        setSubmitting(false);
        setShowConfirmModal(false);
        return;
      }
      
      console.log('Risultato caricamento allegati:', uploadsResult);
      
      // Verifica prima che systemTypes sia valorizzato correttamente
      console.log('VERIFICA PRE-INVIO - formData completo:', formData);
      console.log('VERIFICA PRE-INVIO - tipi impianto disponibili:', formData.systemTypes);
      console.log('VERIFICA PRE-INVIO - manutenzione selezionata:', formData.maintenance);
      if (formData.maintenance?.systemTypes) {
        console.log('VERIFICA PRE-INVIO - systemTypes dalla manutenzione:', formData.maintenance.systemTypes);
      }
      
      // Se systemTypes è vuoto ma abbiamo una manutenzione con i dati, forziamo la valorizzazione
      if ((!formData.systemTypes || formData.systemTypes.length === 0) && 
          formData.maintenance?.systemTypes && formData.maintenance.systemTypes.length > 0) {
        
        console.log('ATTENZIONE: systemTypes vuoto ma manutenzione ha tipi impianto. Recupero forzato...');
        
        // Mappatura di emergenza
        formData.systemTypes = formData.maintenance.systemTypes.map(type => ({
          typeId: type.systemTypeId || type.typeId || type._id || '',
          typeLabel: type.systemTypeLabel || type.typeLabel || type.label || type.name || '',
          testedDate: type.testedDate || null,
          expirationWarrantyDate: type.expirationWarrantyDate || null,
          notes: type.notes || ''
        }));
        
        console.log('RECOVERY - systemTypes dopo recupero forzato:', formData.systemTypes);
      }
      
      // 2. Prepara l'oggetto commessa con i dati dal form
      const jobData = {
        accountId: accountId,
        active: true,
        lastUpdateDate: new Date(),
        
        // Se stiamo creando una nuova commessa, imposta la data di creazione
        // Se stiamo modificando una commessa esistente, mantieni la data originale
        ...(!jobId && { creationDate: new Date() }),
        
        // Dati dal tipo commessa (step 1)
        jobTypeId: formData.jobType?.settingsId || findJobTypeInSettings(formData.jobType)?._id?.toString() || formData.jobType?.id?.toString() || '',
        jobTypeLabel: formData.jobType?.title || formData.jobType?.name || formData.jobType?.label || '',
        
        // Imposta i flag di tipo commessa in base al tipo selezionato
        // Prima controlla nei dati del jobType dalle impostazioni 
        jobTypeSale: determineJobTypeFlag(formData.jobType, 'sales', 'sale', 'jobTypeSale'),
        jobTypeAssistance: determineJobTypeFlag(formData.jobType, 'assistance', 'jobTypeAssistance'),
        jobTypeGeneric: determineJobTypeFlag(formData.jobType, 'generic', 'jobTypeGeneric'),
        jobTypeMaintenance: determineJobTypeFlag(formData.jobType, 'maintenance', 'jobTypeMaintenance'),
        
        // Per una nuova commessa, imposta lo stato predefinito
        // Per l'aggiornamento, mantieni lo stato attuale se presente
        statusId: jobId ? (formData.statusId || findDefaultJobStatus()?._id || '') : findDefaultJobStatus()?._id || '',
        statusLabel: jobId ? (formData.statusLabel || findDefaultJobStatus()?.label || '') : findDefaultJobStatus()?.label || '',
        
        // Dati di dettaglio commessa (step 2)
        // Per commesse di tipo manutenzione, prendiamo i dati del cliente dalla manutenzione selezionata
        customerId: formData.maintenance?.customer?._id ? 
          formData.maintenance.customer._id : 
          (formData.customer?.value || formData.customer?.id || ''),
        
        customerLabel: formData.maintenance?.customer?.label ? 
          formData.maintenance.customer.label : 
          (formData.customer?.label || ''),
        
        // Dati relativi all'impianto
        // Per commesse di tipo assistenza, prendiamo i dati dell'impianto dalla richiesta di assistenza
        // Per commesse di tipo manutenzione, prendiamo i dati dell'impianto dalla manutenzione
        systemId: formData.jobType?.assistance && formData.assistanceRequest ? 
          (formData.assistanceRequest.systemId || '') : 
          formData.maintenance && formData.maintenance.system ? 
          (formData.maintenance.system._id || formData.maintenance.systemId || '') :
          (formData.system?._id || formData.system?.id || ''),
        
        systemLabel: formData.jobType?.assistance && formData.assistanceRequest ? 
          (formData.assistanceRequest.systemLabel || '') : 
          formData.maintenance && formData.maintenance.system ? 
          (formData.maintenance.system.title || formData.maintenance.systemLabel || '') :
          (formData.system?.title || ''),
        
        systemAddressId: formData.jobType?.assistance && formData.assistanceRequest ? 
          (formData.assistanceRequest.systemAddressId || '') : 
          formData.maintenance && formData.maintenance.system ? 
          (formData.maintenance.system.customerAddressId || formData.maintenance.systemAddressId || '') :
          (formData.system?.customerAddressId || ''),
        
        systemAddressLabel: formData.jobType?.assistance && formData.assistanceRequest ? 
          (formData.assistanceRequest.systemAddressLabel || '') : 
          formData.maintenance && formData.maintenance.system ? 
          (formData.maintenance.system.customerAddressLabel || formData.maintenance.systemAddressLabel || '') :
          (formData.system?.customerAddressLabel || ''),
          
        // Per commesse di tipo assistenza, aggiungiamo il systemTypeId dall'assistenza selezionata
        // Per commesse di tipo manutenzione, prendiamo il systemTypeId dalla manutenzione
        // Per altri tipi di commessa, prendiamo il primo tipo di impianto selezionato (se disponibile)
        systemTypeId: formData.jobType?.assistance && formData.assistanceRequest ? 
          (formData.assistanceRequest.systemTypeId || '') : 
          formData.maintenance?.systemTypes && formData.maintenance.systemTypes.length > 0 ?
            (formData.maintenance.systemTypes[0].systemTypeId || formData.maintenance.systemTypes[0].typeId || '') :
          (formData.systemTypes && formData.systemTypes.length > 0 ? 
            // Usa idTipoImpianto se disponibile, altrimenti fallback su typeId
            (formData.systemTypes[0].idTipoImpianto || formData.systemTypes[0].typeId || '') : 
            ''),
        
        // Dati geografici - priorità: 1) impianto dalla system, 2) impianto dalla manutenzione, 3) dati salvati in geoData, 4) valori predefiniti
        // Assicuriamo che geoData sia sempre definito con un tipo valido
        geoData: formData.system?.geoData ? {
          type: formData.system.geoData.type || "Point",  // Assicuriamo che type sia sempre definito
          coordinates: formData.system.geoData.coordinates || [0, 0],
          // Aggiungi sempre distanceKm e distanceTime dai dati dell'impianto se disponibili, altrimenti 0
          distanceKm: formData.system.geoData.distanceKm !== undefined ? formData.system.geoData.distanceKm : 0,
          distanceTime: formData.system.geoData.distanceTime !== undefined ? formData.system.geoData.distanceTime : 0
        } : formData.maintenance?.geoData ? {
          // Se la manutenzione ha dati geoData diretti, li utilizziamo per primi
          type: formData.maintenance.geoData.type || "Point",
          coordinates: formData.maintenance.geoData.coordinates || [0, 0],
          // Recupera distanceKm e distanceTime direttamente dalla manutenzione
          distanceKm: formData.maintenance.geoData.distanceKm !== undefined ? formData.maintenance.geoData.distanceKm : 0,
          distanceTime: formData.maintenance.geoData.distanceTime !== undefined ? formData.maintenance.geoData.distanceTime : 0
        } : formData.maintenance?.system?.geoData ? {
          // Fallback sui dati dell'impianto nella manutenzione
          type: formData.maintenance.system.geoData.type || "Point",
          coordinates: formData.maintenance.system.geoData.coordinates || [0, 0],
          // Aggiungi sempre distanceKm e distanceTime dai dati dell'impianto nella manutenzione
          distanceKm: formData.maintenance.system.geoData.distanceKm !== undefined ? formData.maintenance.system.geoData.distanceKm : 0,
          distanceTime: formData.maintenance.system.geoData.distanceTime !== undefined ? formData.maintenance.system.geoData.distanceTime : 0
        } : formData.geoData ? {
          type: formData.geoData.type || "Point",  // Assicuriamo che type sia sempre definito
          coordinates: formData.geoData.coordinates || [0, 0],
          // Mantieni i valori esistenti se presenti, altrimenti imposta a 0
          distanceKm: formData.geoData.distanceKm !== undefined ? formData.geoData.distanceKm : 0,
          distanceTime: formData.geoData.distanceTime !== undefined ? formData.geoData.distanceTime : 0
        } : {
          // Se non c'è nessun dato geografico, creiamo comunque un oggetto con valori predefiniti
          type: "Point",
          coordinates: [0, 0],
          distanceKm: 0,
          distanceTime: 0
        },
        
        // Lista dei tipi di impianto selezionati
        systemTypeList: (() => {
          console.log('GENERAZIONE systemTypeList - jobType:', formData.jobType);
          console.log('GENERAZIONE systemTypeList - assistanceRequest:', formData.assistanceRequest);
          console.log('GENERAZIONE systemTypeList - formData.systemTypes:', formData.systemTypes);
          console.log('GENERAZIONE systemTypeList - formData.system:', formData.system);
          
          // CASO 1: Se è una commessa di assistenza con assistanceRequest valorizzata
          if (determineJobTypeFlag(formData.jobType, 'assistance') && formData.assistanceRequest) {
            console.log('CREAZIONE systemTypeList per assistenza da assistanceRequest:', {
              systemTypeId: formData.assistanceRequest.systemTypeId,
              systemTypeLabel: formData.assistanceRequest.systemTypeLabel,
              systemTypeExpirationWarrantyDate: formData.assistanceRequest.systemTypeExpirationWarrantyDate
            });
            
            // Anche se formData.systemTypes è vuoto, dobbiamo forzatamente usare i dati da assistanceRequest
            // per le commesse di tipo assistenza
            if (formData.assistanceRequest.systemTypeId && formData.assistanceRequest.systemTypeLabel) {
              return [{
                typeId: formData.assistanceRequest.systemTypeId,
                typeLabel: formData.assistanceRequest.systemTypeLabel,
                testedDate: null, // Di default null per assistenza
                expirationWarrantyDate: formData.assistanceRequest.systemTypeExpirationWarrantyDate || null,
                notes: formData.assistanceRequest.systemTypeNotes || ''
              }];
            }
          }
          
          // CASO 2: Se systemTypes nel form è popolato, lo usiamo
          if (Array.isArray(formData.systemTypes) && formData.systemTypes.length > 0) {
            console.log('CREAZIONE systemTypeList da formData.systemTypes');
            return formData.systemTypes.map(type => ({
              typeId: type.typeId || '',
              typeLabel: type.typeLabel || '',
              testedDate: type.testedDate || null,
              expirationWarrantyDate: type.expirationWarrantyDate || null,
              notes: type.notes || ''
            }));
          }
          
          // CASO 3: Se c'è una manutenzione con systemTypes, li usiamo
          if (formData.maintenance?.systemTypes && formData.maintenance.systemTypes.length > 0) {
            console.log('CREAZIONE systemTypeList da maintenance.systemTypes');
            return formData.maintenance.systemTypes.map(type => ({
              typeId: type.systemTypeId || type.typeId || type._id || '',
              typeLabel: type.systemTypeLabel || type.typeLabel || type.label || type.name || '',
              testedDate: type.testedDate || null,
              expirationWarrantyDate: type.expirationWarrantyDate || null,
              notes: type.notes || ''
            }));
          }
          
          // CASO 4: Se abbiamo un impianto selezionato con typesList, li usiamo direttamente
          if (formData.system?.typesList && Array.isArray(formData.system.typesList) && formData.system.typesList.length > 0) {
            console.log('CREAZIONE systemTypeList da system.typesList:', formData.system.typesList);
            return formData.system.typesList.map(type => ({
              typeId: type.typeId || '',
              typeLabel: type.typeLabel || '',
              testedDate: type.testedDate || null,
              expirationWarrantyDate: type.expirationWarrantyDate || null,
              notes: type.notes || ''
            }));
          }
          
          // CASO 5: Fallback - array vuoto
          console.warn('Nessun dato disponibile per systemTypeList, ritorno array vuoto');
          return [];
        })(),
        
        // Per commesse di assistenza
        // Usiamo i campi _id e code direttamente dalla richiesta di assistenza selezionata
        assistanceRequestId: formData.assistanceRequest?._id || formData.assistanceRequest?.id || '',
        assistanceRequestCode: formData.assistanceRequest?.code || formData.assistanceRequest?.title || '',
        
        // Per commesse di manutenzione
        // Usiamo i campi _id e code direttamente dalla manutenzione selezionata
        maintenanceId: formData.maintenance?._id || '',
        maintenanceCode: formData.maintenance?.code || '',
        
        // Descrizione (step 3)
        description: formData.description || '',
        notes: formData.notes || '',
        
        // Dati di lavorazione (step 4)
        scheduledDate: formData.processingData.dueDate || null,
        
        // Nuovi campi aggiunti allo schema
        employeesNumber: formData.processingData.techniciansCount || 1,
        expectedHours: formData.processingData.estimatedHours || 1,
        dueDate: formData.processingData.dueDate ? new Date(formData.processingData.dueDate) : null,
        
        // Allegati - formattati correttamente secondo lo schema
        uploads: uploadsResult.uploads && Array.isArray(uploadsResult.uploads) ? 
          uploadsResult.uploads.map(upload => {
            // Crea l'oggetto base per il file
            const fileObj = {
              fileUrl: upload.url || upload.fileUrl || upload,
              fileType: upload.fileType || '',
              fileName: upload.fileName || upload.name || ''
            };
            
            // Se è un file esistente, include anche l'ID
            if (upload.fileId) {
              fileObj._id = upload.fileId;
              fileObj.id = upload.fileId;
            }
            
            return fileObj;
          }) : [],
        
        // Altri campi tecnici
        items: [],
        updatedBy: [{
          date: new Date(),
          userId: accountData._id || '',
          userLabel: accountData.username || ''
        }],
        
        // Azioni di manutenzione selezionate
        maintenanceActions: (() => {
          // Se non è una commessa di tipo manutenzione o non ci sono azioni selezionate, restituisci array vuoto
          if (!determineJobTypeFlag(formData.jobType, 'maintenance') || !formData.selectedMaintenanceActions || formData.selectedMaintenanceActions.length === 0) {
            console.log('Nessuna azione di manutenzione selezionata o non è una commessa di manutenzione');
            return [];
          }
          
          // Log delle azioni disponibili e selezionate (per debug)
          console.log('Azioni di manutenzione disponibili:', formData.maintenance?.actions || []);
          console.log('ID azioni selezionate:', formData.selectedMaintenanceActions);
          
          // Filtra e mappa le azioni selezionate
          if (formData.maintenance?.actions && formData.maintenance.actions.length > 0) {
            const selectedActions = formData.maintenance.actions
              .filter(action => formData.selectedMaintenanceActions.includes(action._id || action.id))
              .map(action => ({
                actionId: action._id || action.id,
                title: action.title,
                notes: action.notes || '',
                year: action.year,
                month: action.month,
                statusId: action.statusId,
                statusLabel: action.statusLabel
              }));
              
            console.log('Azioni di manutenzione selezionate e mappate per il salvataggio:', selectedActions);
            return selectedActions;
          }
          
          return [];
        })(),
        
        // Questi campi sono inizializzati ma non valorizzati se si tratta di una nuova commessa
        // Altrimenti manteniamo i valori esistenti
        ...(jobId ? {} : {
          internal: false,
          outbound: false,
          progr: 0,
          year: new Date().getFullYear(),
          finished: false,
          invoiced: false,
          customerCalled: false,
          rent: false,
          fromQuotation: false,
          fatturaPA: false,
          outstanding: false,
          outstandingTotal: false,
          globalTotalPrice: 0,
          globalTotalPriceVatted: 0,
          vatValues: []
        })
      };
      
      // Per la modifica, aggiungiamo l'ID della commessa esistente
      if (jobId) {
        jobData._id = jobId;
      }
      
      // Log completo dell'oggetto job per debug
      console.log(`Oggetto commessa ${jobId ? 'aggiornamento' : 'creazione'}:`, jobData);
      console.log('Uploads presenti nella commessa:', jobData.uploads);
      console.log('Dati geografici copiati:', jobData.geoData);
      console.log('Tipi di impianto aggiunti:', jobData.systemTypeList);
      
      // Controllo di sicurezza: se gli upload sono vuoti ma dovrebbero esserci
      if (formData.attachments.length > 0 && (!jobData.uploads || jobData.uploads.length === 0)) {
        console.warn('AVVISO: Gli allegati sono stati caricati ma non sono presenti nella commessa!');
        toast.warning('Avviso: i file potrebbero non essere stati allegati correttamente alla commessa');
      }
      
      // 3. Chiamata API per creare o aggiornare la commessa
      let response;
      
      // Log completo della manutenzione per debug
      if (formData.maintenance) {
        console.log('STRUTTURA COMPLETA MANUTENZIONE:', formData.maintenance);
      }
      
      // Assicuriamoci che tutti i campi necessari siano valorizzati per commesse di manutenzione
      if (jobData.jobTypeMaintenance === true || (jobData.jobType && jobData.jobType.maintenance === true)) {
        // Forziamo esplicitamente i dati dell'impianto dalla manutenzione
        if (formData.maintenance) {
          // Prima controlliamo se la manutenzione ha gli attributi direttamente (preferito)
          jobData.systemId = formData.maintenance.systemId || '';
          jobData.systemLabel = formData.maintenance.systemLabel || '';
          jobData.systemAddressId = formData.maintenance.systemAddressId || '';
          jobData.systemAddressLabel = formData.maintenance.systemAddressLabel || '';
          jobData.customerId = formData.maintenance.customerId || '';
          jobData.customerLabel = formData.maintenance.customerLabel || '';
          
          console.log('Dati copiati direttamente dalla manutenzione:', {
            systemId: jobData.systemId,
            systemLabel: jobData.systemLabel,
            customerId: jobData.customerId,
            customerLabel: jobData.customerLabel
          });
          
          // Se alcuni dati sono ancora mancanti, proviamo a recuperarli dagli oggetti nidificati
          if ((!jobData.systemId || !jobData.systemLabel) && formData.maintenance.system) {
            jobData.systemId = jobData.systemId || formData.maintenance.system._id || '';
            jobData.systemLabel = jobData.systemLabel || formData.maintenance.system.title || formData.maintenance.system.name || '';
            jobData.systemAddressId = jobData.systemAddressId || formData.maintenance.system.customerAddressId || '';
            jobData.systemAddressLabel = jobData.systemAddressLabel || formData.maintenance.system.customerAddressLabel || '';
          }
          
          if ((!jobData.customerId || !jobData.customerLabel) && formData.maintenance.customer) {
            jobData.customerId = jobData.customerId || formData.maintenance.customer._id || '';
            jobData.customerLabel = jobData.customerLabel || formData.maintenance.customer.label || '';
          }
        }
      }
      
      // CORREZIONE FINALE: accesso diretto ai dati della manutenzione così come vengono restituiti dall'API
      if (jobData.jobTypeMaintenance === true && formData.maintenance) {
        // Force dati cliente
        jobData.customerId = formData.maintenance.customerId || '';
        jobData.customerLabel = formData.maintenance.customerLabel || '';
        
        // Force dati impianto
        jobData.systemId = formData.maintenance.systemId || '';
        jobData.systemLabel = formData.maintenance.systemLabel || '';
        jobData.systemAddressId = formData.maintenance.systemAddressId || '';
        jobData.systemAddressLabel = formData.maintenance.systemAddressLabel || '';
        
        console.log('CORREZIONE FINALE applicata con dati diretti dalla manutenzione');
      }
      
      // Log dettagliato dei dati della commessa prima dell'invio
      console.log('Dati commessa da inviare:', jobData);
      
      // Log specifico per commesse di tipo manutenzione
      if (jobData.jobTypeMaintenance === true || (jobData.jobType && jobData.jobType.maintenance === true)) {
        console.log('DETTAGLI COMMESSA MANUTENZIONE DOPO CORREZIONE FINALE:', {
          'jobTypeMaintenance': jobData.jobTypeMaintenance,
          'maintenanceId': jobData.maintenanceId,
          'systemId': jobData.systemId,
          'systemLabel': jobData.systemLabel,
          'systemAddressId': jobData.systemAddressId,
          'customerId': jobData.customerId,
          'systemTypeId': jobData.systemTypeId,
          'geoData': jobData.geoData
        });
      }
      
      if (jobId) {
        // Modifica di una commessa esistente
        console.log('Aggiornamento commessa esistente con ID:', jobId);
        console.log('Dati inviati al server:', jobData);
        
        // Utilizziamo PATCH invece di PUT per corrispondere all'endpoint del server
        response = await axios.patch(
          `${import.meta.env.VITE_API_BASE_URL || ''}/jobs`,
          { 
            job: {
              ...jobData,
              _id: jobId // Assicuriamoci che l'ID sia incluso nell'oggetto job
            }
          },
          {
            headers: {
              'auth-token': token,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        // Creazione di una nuova commessa
        response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL || ''}/jobs`,
          { job: jobData },
          {
            headers: {
              'auth-token': token,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      // Log della risposta per debug
      console.log(`Risposta ${jobId ? 'aggiornamento' : 'creazione'} commessa:`, response.data);
      
      if (response.data && (response.data.result || response.data._id)) {
        // Chiudi il modale
        setShowConfirmModal(false);
        
        // Mostra messaggio di successo
        toast.success(`Commessa ${jobId ? 'aggiornata' : 'creata'} con successo`);
        handleAfterSave(); // Usa la nuova funzione invece di navigate
      } else {
        throw new Error('Risposta non valida dal server');
      }
    } catch (error) {
      console.error(`Errore durante ${jobId ? 'l\'aggiornamento' : 'la creazione'} della commessa:`, error);
      
      // Log dettagliato dell'errore per debug
      if (error.response) {
        console.error('Dettagli errore risposta:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        });
      }
      
      toast.error(`Errore: ${error.response?.data?.message || error.message || 'Errore sconosciuto'}`);
    } finally {
      setSubmitting(false);
      if (showConfirmModal) setShowConfirmModal(false);
    }
  };
  
  // Funzione per trovare lo stato predefinito per la commessa
  const findDefaultJobStatus = () => {
    if (!jobStatuses || jobStatuses.length === 0) return null;
    
    // Cerca lo stato con default = true
    const defaultStatus = jobStatuses.find(status => status.default === true);
    
    // Se non ne trova uno con default = true, restituisce il primo
    return defaultStatus || jobStatuses[0];
  };
  
  // Funzione per trovare il tipo di commessa nelle impostazioni dell'account
  const findJobTypeInSettings = (selectedJobType) => {
    if (!selectedJobType) return null;
    
    // Cerca il tipo di commessa con id corrispondente
    const foundJobType = jobTypesSettings.find(jt => 
      jt._id === selectedJobType.id || 
      jt._id === selectedJobType.settingsId ||
      jt.category === selectedJobType.category
    );
    
    return foundJobType;
  };
  
  // Funzione per verificare se un tipo di commessa è selezionato
  const isJobTypeSelected = (type) => {
    if (!formData.jobType || !type) return false;
    
    // Usa solo corrispondenze specifiche ed esatte
    // Verifica le corrispondenze sull'id MongoDB
    if (type._id && formData.jobType._id) {
      return type._id === formData.jobType._id;
    }
    
    // Verifica corrispondenza con settingsId
    if (type._id && formData.jobType.settingsId) {
      return type._id === formData.jobType.settingsId;
    }
    
    // Fallback per compatibilità con id numerico
    if (type.id && formData.jobType.id) {
      return type.id === formData.jobType.id;
    }
    
    return false;
  };
  
  // Funzione per determinare il valore di un flag del tipo di commessa
  const determineJobTypeFlag = (jobType, ...possibleKeys) => {
    if (!jobType) return false;
    
    // Prova tutte le possibili chiavi
    for (const key of possibleKeys) {
      // Se la chiave esiste ed è un booleano, usa quel valore
      if (typeof jobType[key] === 'boolean') {
        return jobType[key];
      }
      // Se la chiave esiste ed è una stringa, controlla se è 'true', '1', ecc.
      else if (typeof jobType[key] === 'string') {
        const value = jobType[key].toLowerCase();
        if (value === 'true' || value === '1' || value === 'yes') {
          return true;
        }
      }
    }
    
    // Controlla in categorie e titoli
    if (jobType.category) {
      const category = jobType.category.toLowerCase();
      for (const key of possibleKeys) {
        if (category.includes(key.toLowerCase())) {
          return true;
        }
      }
    }
    
    if (jobType.title || jobType.name || jobType.label) {
      const title = (jobType.title || jobType.name || jobType.label).toLowerCase();
      for (const key of possibleKeys) {
        if (title.includes(key.toLowerCase())) {
          return true;
        }
      }
    }
    
    return false;
  };
  
  // Monitoraggio dettagliato dei tipi commessa per debug
  useEffect(() => {
    console.log('Stati commessa disponibili:', jobStatuses);
    console.log('Tipi commessa:', jobTypesSettings);
    console.log('AccountData:', accountData);
    
    if (jobTypesSettings && jobTypesSettings.length > 0) {
      console.log('Primo tipo commessa:', jobTypesSettings[0]);
      console.log('Properties del primo tipo:', Object.keys(jobTypesSettings[0]));
    } else {
      console.warn('Nessun tipo di commessa disponibile');
    }
  }, [jobStatuses, jobTypesSettings, accountData]);
  
  // Funzione per validare Step 1 (tipo commessa)
  const validateStepOne = (data) => {
    if (!data.jobType) {
      return false;
    }
    return true;
  };
  
  // Funzione per validare Step 2 (dettaglio commessa)
  const validateStepTwo = (data) => {
    if (determineJobTypeFlag(data.jobType, 'maintenance') && !data.maintenance) {
      return false;
    }
    
    if (data.jobType?.assistance && !data.assistanceRequest) {
      return false;
    }
    
    if (data.jobType?.generic && !data.customer) {
      return false;
    }
    
    // Per commesse generiche è sufficiente selezionare il cliente
    if (data.jobType?.generic) {
      return true;
    }
    
    // Per commesse di manutenzione è sufficiente selezionare la manutenzione
    if (determineJobTypeFlag(data.jobType, 'maintenance')) {
      return true;
    }
    
    if (!data.jobType?.generic && !data.jobType?.assistance) {
      if (!data.customer) {
        return false;
      }
      if (!data.system) {
        return false;
      }
      if (!data.systemTypes || data.systemTypes.length === 0) {
        return false;
      }
    }
    
    return true;
  };
  
  // Funzione per validare Step 3 (descrizione) - non obbligatoria
  const validateStepThree = (data) => {
    // La descrizione non è più obbligatoria
    return true;
  };
  
  // Validazione dello step corrente con feedback visuale (toast)
  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0: // Step tipo commessa
        if (!validateStepOne(formData)) {
          toast.error('Seleziona un tipo di commessa');
          return false;
        }
        return true;
        
      case 1: // Step dettaglio commessa
        if (determineJobTypeFlag(formData.jobType, 'maintenance') && !formData.maintenance) {
          toast.error('Seleziona una manutenzione');
          return false;
        }
        
        if (formData.jobType?.assistance && !formData.assistanceRequest) {
          toast.error('Seleziona una richiesta di assistenza');
          return false;
        }
        
        if (formData.jobType?.generic && !formData.customer) {
          toast.error('Seleziona un cliente');
          return false;
        }
        
        // Per commesse generiche è sufficiente selezionare il cliente
        if (formData.jobType?.generic) {
          return true;
        }
        
        // Per commesse di manutenzione è sufficiente selezionare la manutenzione
        if (determineJobTypeFlag(formData.jobType, 'maintenance')) {
          return true;
        }
        
        if (!formData.jobType?.generic && !formData.jobType?.assistance) {
          if (!formData.customer) {
            toast.error('Seleziona un cliente');
            return false;
          }
          if (!formData.system) {
            toast.error('Seleziona un impianto');
            return false;
          }
          if (!formData.systemTypes || formData.systemTypes.length === 0) {
            toast.error('Seleziona almeno un tipo di impianto');
            return false;
          }
        }
        
        return true;
        
      case 2: // Step descrizione - non obbligatoria
        // La descrizione non è più obbligatoria, passa sempre
        return true;
        
      case 3: // Step dati lavorazione
        // Placeholder per ora
        return true;
        
      default:
        return true;
    }
  };
  
  // Funzione per aggiornare il formData
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Effetto per gestire la creazione di un nuovo cliente
  useEffect(() => {
    // Quando rightModalIsRefreshNow diventa true e stavamo creando un cliente
    if (rightModalIsRefreshNow && wasCreatingCustomer) {
      console.log('Refresh rilevato dopo creazione cliente');
      console.log('Dati cliente da RightModal (lastClosedData):', rightModalLastClosedData);
      
      // Se abbiamo i dati del cliente appena creato, usali
      if (rightModalLastClosedData && rightModalLastClosedData.customerId && rightModalLastClosedData.customerLabel) {
        const customerForSelect = {
          id: rightModalLastClosedData.customerId,
          value: rightModalLastClosedData.customerId,
          label: rightModalLastClosedData.customerLabel
        };
        
        console.log('Cliente selezionato:', customerForSelect);
        updateFormData('customer', customerForSelect);
        toast.success(`Cliente "${customerForSelect.label}" selezionato`);
        
        // Pulisci i dati dopo averli consumati
        dispatch(clearLastClosedData());
      } else {
        console.warn('Dati cliente non disponibili in rightModalLastClosedData:', rightModalLastClosedData);
      }
      
      // Reset del flag
      setWasCreatingCustomer(false);
      dispatch(resetRefreshNow());
    }
    
    // Quando rightModalIsRefreshNow diventa true e stavamo creando un impianto
    if (rightModalIsRefreshNow && wasCreatingSystem) {
      console.log('Refresh rilevato dopo creazione impianto');
      console.log('Dati impianto da RightModal (lastClosedData):', rightModalLastClosedData);
      
      // Se abbiamo i dati dell'impianto appena creato, usali
      if (rightModalLastClosedData && rightModalLastClosedData.systemData) {
        let systemData = rightModalLastClosedData.systemData;
        
        console.log('Impianto da selezionare:', systemData);
        console.log('Type of systemData:', typeof systemData);
        
        // Gestisci il caso in cui systemData sia solo un ID stringa (per retrocompatibilità)
        if (typeof systemData === 'string') {
          console.warn('systemData è una stringa (ID), impossibile selezionare automaticamente. Attendo il ricaricamento della lista.');
          systemData = null;
        }
        
        // Prima ricarica la lista degli impianti per includere quello appena creato
        console.log('Verifica cliente presente:', formData.customer);
        console.log('formData.customer?.id:', formData.customer?.id);
        console.log('formData.customer?.value:', formData.customer?.value);
        
        if (formData.customer?.id || formData.customer?.value) {
          const customerIdForFetch = formData.customer.id || formData.customer.value;
          console.log('Cliente presente, avvio ricaricamento lista impianti per cliente ID:', customerIdForFetch);
          
          // Usa setTimeout per dare tempo al backend di salvare
          setTimeout(async () => {
            console.log('Esecuzione setTimeout - ricaricamento impianti per cliente:', customerIdForFetch);
            try {
              const systems = await fetchCustomerSystems(customerIdForFetch);
              console.log('Lista impianti ricaricata, totale:', systems?.length || customerSystems.length);
              
              // Se abbiamo l'oggetto completo, selezionalo
              if (systemData && systemData._id) {
                console.log('Selezione impianto dopo ricarica lista - ID:', systemData._id);
                updateFormData('system', systemData);
                
                // Scroll automatico verso la lista impianti per renderlo visibile
                setTimeout(() => {
                  const systemElement = document.querySelector(`[data-system-id="${systemData._id}"]`);
                  if (systemElement) {
                    systemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    console.log('Scroll automatico verso impianto creato');
                  }
                }, 200);
                
                toast.success(`Impianto "${rightModalLastClosedData.systemLabel}" selezionato e aggiunto alla lista`);
              } else {
                // Se non abbiamo l'oggetto, cerca l'ultimo impianto creato nella lista ricaricata
                const newSystemId = typeof rightModalLastClosedData.systemData === 'string' 
                  ? rightModalLastClosedData.systemData 
                  : rightModalLastClosedData.systemId;
                  
                if (newSystemId && customerSystems.length > 0) {
                  const foundSystem = customerSystems.find(s => s._id === newSystemId);
                  if (foundSystem) {
                    console.log('Impianto trovato nella lista ricaricata:', foundSystem);
                    updateFormData('system', foundSystem);
                    toast.success(`Impianto "${rightModalLastClosedData.systemLabel}" aggiunto e selezionato`);
                  } else {
                    console.warn('Impianto non trovato nella lista ricaricata');
                    toast.info('Impianto creato con successo');
                  }
                } else {
                  toast.success('Impianto creato con successo');
                }
              }
            } catch (error) {
              console.error('Errore durante il ricaricamento impianti:', error);
              toast.error('Errore durante il ricaricamento della lista impianti');
            }
            
            // Pulisci i dati dopo averli consumati
            dispatch(clearLastClosedData());
          }, 500); // Delay di 500ms per dare tempo al backend
        } else {
          // Se non c'è un cliente (caso improbabile)
          console.warn('ATTENZIONE: Cliente non presente in formData, impossibile ricaricare lista impianti');
          console.warn('formData completo:', formData);
          if (systemData && systemData._id) {
            updateFormData('system', systemData);
            toast.success(`Impianto "${rightModalLastClosedData.systemLabel}" selezionato`);
          } else {
            toast.success('Impianto creato con successo');
          }
          dispatch(clearLastClosedData());
        }
      } else {
        console.warn('Dati impianto non disponibili in rightModalLastClosedData:', rightModalLastClosedData);
      }
      
      // Reset del flag
      setWasCreatingSystem(false);
      dispatch(resetRefreshNow());
    }
  }, [rightModalIsRefreshNow, wasCreatingCustomer, wasCreatingSystem, rightModalLastClosedData, dispatch, formData.customer]);
  
  // Funzione per caricare gli impianti di un cliente
  const fetchCustomerSystems = async (customerId) => {
    if (!customerId || !accountId || !token) {
      console.warn('Impossibile caricare gli impianti: parametri mancanti', { customerId, accountId });
      return Promise.reject('Parametri mancanti');
    }
    
    setCustomerSystemsLoading(true);
    
    try {
      // Chiamata API reale usando lo stesso pattern delle altre chiamate nel progetto
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/systems/systemsList`;
      
      console.log('Chiamata API fetchCustomerSystems:', apiUrl, 'con parametri:', {
        accountId,
        customerId
      });
      
      const response = await axios.get(apiUrl, {
        headers: {
          'auth-token': token
        },
        params: {
          accountId: accountId,
          customerId: customerId
        }
      });
      
      console.log('Risposta API impianti:', response);

      let systemsData = [];
      
      // Gestisci diversi formati di risposta possibili
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Formato Array diretto
          systemsData = response.data;
          setCustomerSystems(response.data);
          console.log('Impianti trovati (array):', response.data.length, 'Primo impianto:', response.data[0]?._id);
        } else if (response.data.result && Array.isArray(response.data.result)) {
          // Formato { result: [...] }
          systemsData = response.data.result;
          setCustomerSystems(response.data.result);
          console.log('Impianti trovati (result):', response.data.result.length);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Formato { data: [...] }
          systemsData = response.data.data;
          setCustomerSystems(response.data.data);
          console.log('Impianti trovati (data):', response.data.data.length, 'Primo impianto:', response.data.data[0]?._id);
        } else {
          // Gestisci altri formati di risposta
          console.warn('Formato risposta non riconosciuto:', response.data);
          setCustomerSystems([]);
        }
      } else {
        setCustomerSystems([]);
        console.warn('Nessun dato nella risposta API');
      }
      
      setCustomerSystemsLoading(false);
      
      // Log dettagliato degli ID disponibili per facilitare il debug
      if (systemsData.length > 0) {
        console.log('IDs di impianti disponibili:', systemsData.map(sys => sys._id));
      }
      
      return Promise.resolve(systemsData);
    } catch (error) {
      console.error('Errore nel caricamento degli impianti:', error);
      setCustomerSystems([]);
      setCustomerSystemsLoading(false);
      return Promise.reject(error);
    }
  };

  // Effetto per caricare gli impianti del cliente quando cambia
  useEffect(() => {
    if (formData.customer?.value && accountId) {
      // Reset del sistema e tipi selezionati quando cambia il cliente
      updateFormData('system', null);
      updateFormData('systemTypes', []);
      
      // Carica gli impianti del cliente
      fetchCustomerSystems(formData.customer.value);
    } else {
      setCustomerSystems([]);
    }
  }, [formData.customer, accountId, token]);
  
  // Effetto per garantire la persistenza dei dati di manutenzione in modalità modifica
  useEffect(() => {
    if (jobId && formData.selectedMaintenanceActions && formData.selectedMaintenanceActions.length > 0 && !formData.maintenance) {
      console.log('Rilevata INCONSISTENZA: azioni di manutenzione presenti ma oggetto manutenzione mancante');
      
      // Se abbiamo il maintenanceId nel formData o nella URL, ricarica i dettagli
      const maintenanceIdToLoad = formData.maintenanceId || formData._maintenanceId;
      
      if (maintenanceIdToLoad) {
        console.log('Tentativo di recupero manutenzione con ID:', maintenanceIdToLoad);
        
        // Ricarica i dettagli della manutenzione
        fetchMaintenanceDetails(maintenanceIdToLoad).then(maintenanceData => {
          if (maintenanceData) {
            // Normalizza e prepara l'oggetto manutenzione
            const enhancedMaintenance = {
              ...maintenanceData,
              id: maintenanceData._id,
              title: maintenanceData.code || maintenanceData.title || 'Manutenzione',
              value: maintenanceData._id,
              selected: true,
              isSelected: true,
              actions: Array.isArray(maintenanceData.actions) ? maintenanceData.actions.map(action => ({
                ...action,
                _id: action._id || action.id,
                id: action.id || action._id,
                actionId: action._id || action.id
              })) : []
            };
            
            console.log('Manutenzione recuperata e ripristinata:', enhancedMaintenance.title);
            
            // Aggiorna il form con la manutenzione recuperata
            setFormData(prev => ({
              ...prev,
              maintenance: enhancedMaintenance,
              maintenanceId: maintenanceData._id
            }));
          }
        }).catch(err => {
          console.error('Errore nel recupero dati manutenzione:', err);
        });
      }
    }
  }, [jobId, formData.selectedMaintenanceActions, formData.maintenance]);
  
  // Effect per caricare le richieste di assistenza quando necessario
  useEffect(() => {
    // Carica le assistenze se è selezionato un tipo commessa di assistenza
    if (formData.jobType?.assistance && accountId && token) {
      // In modalità edit non carichiamo la lista, perché l'assistenza specifica viene caricata
      // già quando si carica la commessa (nell'altro useEffect)
      if (jobId) {
        // In modalità edit non facciamo nulla qui, l'assistenza è già caricata
        // nel momento in cui viene caricata la commessa
        return;
      }
      
      // Solo in modalità creazione carichiamo la lista delle assistenze per la ricerca
      setAssistanceRequestsLoading(true);
      
      // Costruisci i parametri della query
      const params = {
        accountId: accountId,
        length: 50
      };
      
      // Aggiungi parametro search solo se ci sono almeno 3 caratteri
      if (assistanceSearchTerm && assistanceSearchTerm.length >= 3) {
        params.search = assistanceSearchTerm;
      } else if (!assistanceSearchTerm || assistanceSearchTerm.length < 3) {
        // Se non c'è un termine di ricerca valido, non facciamo la chiamata
        setAssistanceRequestsLoading(false);
        setAssistanceRequests([]);
        return;
      }
      
      console.log('Caricamento lista assistenze per ricerca...');
      axios.get(`${import.meta.env.VITE_API_BASE_URL}/assistanceRequests/list`, {
        params: params,
        headers: { 'auth-token': token }
      })
      .then(response => {
        console.log('API lista richieste assistenza risposta:', response);
        
        if (response.data) {
          // Gestione dei diversi formati di risposta possibili
          if (Array.isArray(response.data)) {
            setAssistanceRequests(response.data);
          } else if (response.data.data && Array.isArray(response.data.data)) {
            setAssistanceRequests(response.data.data);
          } else {
            console.warn('Formato risposta assistenze non riconosciuto:', response.data);
            setAssistanceRequests([]);
          }
        } else {
          setAssistanceRequests([]);
          console.warn('Nessun dato nella risposta API assistenze');
        }
      })
      .catch(error => {
        console.error('Errore nel caricamento delle richieste di assistenza:', error);
        toast.error('Errore nel caricamento delle richieste di assistenza');
        setAssistanceRequests([]);
      })
      .finally(() => {
        setAssistanceRequestsLoading(false);
      });
    }
  }, [formData.jobType, accountId, token, assistanceSearchTerm, jobId]);
  
  // Effect che gestisce il debounce per la ricerca delle assistenze
  useEffect(() => {
    // Effettua la ricerca solo se ci sono almeno 3 caratteri
    if (assistanceSearchTerm.length >= 3 || assistanceSearchTerm.length === 0) {
      const timeoutId = setTimeout(() => {
        // La ricerca verrà attivata dall'effect precedente quando assistanceSearchTerm cambia
      }, 500); // 500ms di debounce
      
      return () => clearTimeout(timeoutId);
    }
  }, [assistanceSearchTerm]);
  
  // Effect per caricare le manutenzioni quando il tipo di commessa è "manutenzione"
  useEffect(() => {
    if (formData.jobType && determineJobTypeFlag(formData.jobType, 'maintenance') && accountId) {
      setMaintenancesLoading(true);
      updateFormData('maintenance', null);
      
      // API per recuperare le manutenzioni
      const apiUrl = `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances/list`;
      
      console.log('Caricamento manutenzioni:', apiUrl);
      
      axios.get(apiUrl, {
        headers: {
          'auth-token': token
        },
        params: {
          accountId: accountId,
          search: maintenanceSearchTerm.length >= 3 ? maintenanceSearchTerm : '',
          start: 0,
          length: 10 // Carica le ultime 10 manutenzioni
        }
      })
      .then(response => {
        console.log('Risposta API manutenzioni:', response);
        
        // Gestione dei diversi formati di risposta possibili
        if (response.data) {
          if (Array.isArray(response.data.data)) {
            setMaintenances(response.data.data);
          } else if (response.data.result && Array.isArray(response.data.result)) {
            setMaintenances(response.data.result);
          } else {
            console.warn('Formato risposta manutenzioni non riconosciuto:', response.data);
            setMaintenances([]);
          }
        } else {
          setMaintenances([]);
          console.warn('Nessun dato nella risposta API manutenzioni');
        }
      })
      .catch(error => {
        console.error('Errore nel caricamento delle manutenzioni:', error);
        toast.error('Errore nel caricamento delle manutenzioni');
        setMaintenances([]);
      })
      .finally(() => {
        setMaintenancesLoading(false);
      });
    }
  }, [formData.jobType, accountId, token, maintenanceSearchTerm]);
  
  // Effect che gestisce il debounce per la ricerca delle manutenzioni
  useEffect(() => {
    // Effettua la ricerca solo se ci sono almeno 3 caratteri
    if (maintenanceSearchTerm.length >= 3 || maintenanceSearchTerm.length === 0) {
      const timeoutId = setTimeout(() => {
        // La ricerca verrà attivata dall'effect precedente quando maintenanceSearchTerm cambia
      }, 500); // 500ms di debounce
      
      return () => clearTimeout(timeoutId);
    }
  }, [maintenanceSearchTerm]);
  
  // Componente per visualizzare i dettagli dell'assistenza in modo sicuro
  const AssistanceDetails = ({ assistance, onRemove, readOnly }) => {
    if (!assistance) return (
      <div className="py-4 text-center text-gray-500">
        <p>Informazioni sulla richiesta di assistenza non disponibili</p>
      </div>
    );
    
    return (
      <div className="bg-white p-4 rounded-lg border border-blue-100 mt-2">
        {/* Intestazione con codice e scadenza */}
        <div className="flex justify-between items-start">
          <span className="font-bold text-blue-700 text-lg">
            {assistance.code || `#${assistance._id || 'N/D'}`}
          </span>
          <div className="flex flex-col gap-1">
            {assistance.assistanceTypePriorityLabel && (
              <span className={`text-xs px-2 py-1 rounded-md text-center ${assistance.assistanceTypePriorityValue === 'high' ? 'bg-red-100 text-red-800' : assistance.assistanceTypePriorityValue === 'medium' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                Priorità: {assistance.assistanceTypePriorityLabel}
              </span>
            )}
            {assistance.expirationDate && (
              <span className={`text-xs px-2 py-1 rounded-md ${new Date(assistance.expirationDate) < new Date() ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                Scadenza: {new Date(assistance.expirationDate).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        
        {/* Informazioni sul cliente */}
        <div className="mt-4 pt-3 border-t">
          <h5 className="font-medium text-gray-700 mb-2">Informazioni cliente</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <span className="font-medium text-sm">Cliente:</span>
              <span className="text-gray-800 ml-2">{assistance.customerLabel || 'N/D'}</span>
            </div>
            
            {assistance.systemLabel && (
              <div>
                <span className="font-medium text-sm">Impianto:</span>
                <span className="text-gray-800 ml-2">{assistance.systemLabel}</span>
              </div>
            )}
            
            {assistance.systemTypeLabel && (
              <div>
                <span className="font-medium text-sm">Tipo impianto:</span>
                <span className="text-gray-800 ml-2">
                  {assistance.systemTypeLabel}
                  {assistance.systemTypeWarranty !== undefined && (
                    <span className="ml-2">
                      {assistance.systemTypeWarranty ? (
                        assistance.systemTypeExpirationWarrantyDate ? (
                          <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md ${new Date(assistance.systemTypeExpirationWarrantyDate) < new Date() ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {new Date(assistance.systemTypeExpirationWarrantyDate) < new Date() ? 'Garanzia scaduta' : 'In garanzia'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-md">In garanzia</span>
                        )
                      ) : (
                        <span className="inline-flex items-center text-xs px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md">Non in garanzia</span>
                      )}
                    </span>
                  )}
                </span>
              </div>
            )}
            
            {assistance.systemAddressLabel && (
              <div>
                <span className="font-medium text-sm">Indirizzo:</span>
                <span className="text-gray-800 ml-2">{assistance.systemAddressLabel}</span>
              </div>
            )}
            
            <div>
              <span className="font-medium text-sm">Data creazione:</span>
              <span className="text-gray-800 ml-2">{new Date(assistance.creationDate).toLocaleDateString()}</span>
            </div>
            
            {assistance.paymentTypeLabel && (
              <div>
                <span className="font-medium text-sm">Tipo pagamento:</span>
                <span className="text-gray-800 ml-2">{assistance.paymentTypeLabel}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Casi d'uso e richieste */}
        {assistance.useCases && assistance.useCases.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <h5 className="font-medium text-gray-700 mb-2">Casi d'uso</h5>
            <div className="flex flex-wrap gap-2">
              {assistance.useCases.map((useCase, index) => {
                // Estrai il testo da mostrare in modo sicuro
                let useCaseText = '';
                
                if (typeof useCase === 'string') {
                  useCaseText = useCase;
                } else if (useCase && typeof useCase === 'object') {
                  // Cerca le proprietà in ordine di priorità
                  useCaseText = useCase.title || useCase.label || useCase.name || 
                                useCase.useCase || useCase.id || 
                                (useCase.toString && useCase.toString() !== '[object Object]' ? 
                                  useCase.toString() : 'Caso d\'uso');
                }
                
                return (
                  <span key={index} className="bg-gray-100 text-gray-800 px-2 py-1 rounded-md text-sm">
                    {useCaseText}
                  </span>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Descrizione del cliente */}
        {assistance.customerRequest && (
          <div className="mt-4 pt-3 border-t">
            <h5 className="font-medium text-gray-700 mb-2">Descrizione del cliente</h5>
            <p className="text-gray-800 text-sm">{assistance.customerRequest}</p>
          </div>
        )}
        
        {/* Note */}
        {assistance.notes && (
          <div className="mt-4 pt-3 border-t">
            <h5 className="font-medium text-gray-700 mb-2">Note</h5>
            <p className="text-gray-800 text-sm">{assistance.notes}</p>
          </div>
        )}
        
        {/* Allegati */}
        {assistance.attachments && assistance.attachments.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <h5 className="font-medium text-gray-700 mb-2">Allegati</h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {assistance.attachments.map((attachment, idx) => (
                <div key={idx} className="border rounded p-2 flex items-center space-x-2 bg-gray-50">
                  <div style={{ 
                    backgroundColor: palette.primaryBg || palette.primary + '15',
                    padding: '0.5rem', 
                    borderRadius: '0.25rem', 
                    color: palette.primary 
                  }}>
                    <i className="fa-solid fa-paperclip"></i>
                  </div>
                  <div className="flex-1 truncate">
                    <a 
                      href={attachment.url || `#no-url-${idx}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: palette.primary, 
                        '&:hover': { textDecoration: 'underline' } 
                      }}
                    >
                      {attachment.filename || attachment.name || `Allegato ${idx + 1}`}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Pulsante per tornare alla lista, solo se non è in sola lettura */}
        {!readOnly && (
          <div className="mt-4 text-sm">
            <button 
              className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
              onClick={onRemove}
              type="button"
            >
              <i className="fa-solid fa-arrow-left mr-1"></i> Torna alla lista
            </button>
          </div>
        )}
      </div>
    );
  };
  
  // Renderizzazione degli step
  const renderStep = () => {
    switch (currentStep) {
      case 0: // Step 1: Tipo commessa
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold" style={{ color: palette.primary }}>
              {jobId ? "Tipo di commessa" : "Scegli il tipo di commessa"}
            </h2>
            
            {/* In modalità edit, mostriamo la selezione corrente con maggiore evidenza */}
            {jobId && formData.jobType && (
              <div className="mb-6">
                <div style={{ 
                  backgroundColor: palette.warningBg || '#fff8e6', 
                  borderColor: palette.warningBorder || '#ffeeba',
                  borderWidth: '1px', 
                  borderStyle: 'solid',
                  padding: '0.75rem', 
                  borderRadius: '0.375rem', 
                  marginBottom: '1rem' 
                }}>
                  <p style={{ color: palette.warningText || '#856404' }} className="flex items-center">
                    <DynamicIcon iconClass="fa-solid fa-info-circle mr-2" />
                    Il tipo di commessa non può essere modificato una volta creata la commessa.
                  </p>
                </div>
                
                <div style={{ 
                  borderWidth: '2px', 
                  borderStyle: 'solid',
                  borderColor: palette.primary, 
                  backgroundColor: palette.primaryBg || palette.primary + '15', 
                  borderRadius: '0.5rem',
                  padding: '1rem',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 style={{ color: palette.primary, fontSize: '1.125rem', fontWeight: 'bold' }}>
                        {formData.jobType.title || formData.jobType.name || formData.jobType.label}
                      </h3>
                      <p style={{ color: palette.primaryText || palette.text, fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {determineJobTypeFlag(formData.jobType, 'assistance') && 'Richiesta di assistenza'}
                        {determineJobTypeFlag(formData.jobType, 'generic') && 'Commessa generica'}
                        {determineJobTypeFlag(formData.jobType, 'maintenance') && 'Commessa di manutenzione'}
                        {!determineJobTypeFlag(formData.jobType, 'assistance') && 
                         !determineJobTypeFlag(formData.jobType, 'generic') && 
                         !determineJobTypeFlag(formData.jobType, 'maintenance') && 
                         (formData.jobType.description || formData.jobType.category || 'Tipo di commessa personalizzato')}
                      </p>
                    </div>
                    <span style={{ 
                      backgroundColor: palette.primaryBg || palette.primary + '22',
                      color: palette.primary,
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.875rem',
                      fontWeight: '500'
                    }}>
                      Selezionato
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Selettore tipo commessa - visibile solo in modalità creazione o se non c'è ancora un tipo selezionato */}
            {(!jobId || !formData.jobType) && (
              <div className="grid grid-cols-2 gap-4">
                {jobTypesSettings && jobTypesSettings.length > 0 ? jobTypesSettings
                  .filter(type => !determineJobTypeFlag(type, 'sales', 'sale'))
                  .map(type => {
                  // Determina se il tipo è selezionato
                  const isSelected = isJobTypeSelected(type);
                  return (
                    <div 
                      key={type._id || type.id}
                      style={{ 
                        border: `1px solid ${isSelected ? palette.primary : palette.neutralLighter || '#e2e8f0'}`,
                        borderRadius: '0.5rem',
                        padding: '1rem',
                        backgroundColor: isSelected ? palette.primaryBg || palette.primary + '15' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontWeight: isSelected ? '500' : 'normal'
                      }}
                      onClick={() => {
                        // Se stiamo usando i jobTypesSettings, l'_id è già presente
                        // altrimenti cerchiamo una corrispondenza
                        let enhancedType = type;
                        
                        if (!type._id && jobTypesSettings && jobTypesSettings.length > 0) {
                          // Cerchiamo il tipo di commessa nelle impostazioni dell'account
                          const matchingSettingType = jobTypesSettings.find(jt => 
                            jt.id === type.id || 
                            jt.category === type.category ||
                            jt.title === type.title
                          );
                          
                          // Se troviamo una corrispondenza, aggiungiamo l'_id al tipo di commessa
                          if (matchingSettingType) {
                            enhancedType = { ...type, settingsId: matchingSettingType._id };
                          }
                        } else if (type._id) {
                          // Se l'_id è già presente, lo salviamo come settingsId per compatibilità
                          enhancedType = { ...type, settingsId: type._id };
                        }
                        
                        console.log('Tipo commessa selezionato:', enhancedType);
                        
                        // Reset delle selezioni dipendenti
                        updateFormData('jobType', enhancedType);
                        updateFormData('customer', null);
                        updateFormData('system', null);
                        updateFormData('systemType', null);
                        updateFormData('assistanceRequest', null);
                        updateFormData('maintenance', null);
                      }}
                    >
                      <h3 style={{ fontWeight: '600', color: isSelected ? palette.primary : palette.text }}>
                        {type.title || type.name || type.label}
                      </h3>
                      <p style={{ 
                        fontSize: '0.875rem', 
                        color: isSelected ? palette.primaryText || palette.text : palette.neutralText || '#718096',
                        marginTop: '0.25rem' 
                      }}>
                        {determineJobTypeFlag(type, 'assistance') && 'Richiesta di assistenza'}
                        {determineJobTypeFlag(type, 'generic') && 'Commessa generica'}
                        {determineJobTypeFlag(type, 'maintenance') && 'Commessa di manutenzione'}
                        {!determineJobTypeFlag(type, 'assistance') && 
                         !determineJobTypeFlag(type, 'generic') && 
                         !determineJobTypeFlag(type, 'maintenance') && 
                         (type.description || type.category || 'Tipo di commessa personalizzato')}
                      </p>
                    </div>
                  );
                }) : (
                  <div 
                    className="col-span-2 p-8 text-center rounded-lg"
                    style={{ backgroundColor: palette.neutralLightest || '#f9fafb' }}
                  >
                    <p style={{ color: palette.neutralText || '#718096' }}>
                      Caricamento tipi di commessa...
                    </p>
                  </div>
                )}
            </div>
            )}
          </div>
        );

      case 1: // Step 2: Dettaglio commessa
        // Log dettagliato all'inizio dello step 2 per verificare lo stato dei dati
        if (determineJobTypeFlag(formData.jobType, 'maintenance')) {
          console.log('=== DEBUG STEP 2 - COMMESSA MANUTENZIONE ===');
          console.log('jobId:', jobId);
          console.log('formData.maintenance:', formData.maintenance);
          console.log('formData.maintenanceId:', formData.maintenanceId);
          console.log('formData.selectedMaintenanceActions:', formData.selectedMaintenanceActions);
          if (formData.maintenance && formData.maintenance.actions) {
            console.log(`Azioni disponibili nella manutenzione: ${formData.maintenance.actions.length}`);
            formData.maintenance.actions.forEach((action, idx) => {
              console.log(`Azione ${idx + 1}: ID=${action._id || action.id}, Title=${action.title}, Status=${action.statusLabel || 'N/D'}`);
            });
          }
          console.log('=== FINE DEBUG STEP 2 ===');
        }
        
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold" style={{ color: palette.primary }}>Dettaglio commessa</h2>
                        {/* Contenuto condizionale in base al tipo di commessa */}
            {determineJobTypeFlag(formData.jobType, 'maintenance') && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2" style={{ color: palette.text }}>
                  {jobId ? "Manutenzione selezionata" : "Seleziona manutenzione"}
                </h3>
                
                {/* Se è una nuova commessa oppure non c'è già una manutenzione selezionata, mostra la ricerca */}
                {!jobId && (
                  <div className="mb-3">
                    <input
                      type="text"
                      className="w-full mb-2"
                      placeholder="Cerca manutenzione (almeno 3 caratteri)..."
                      value={maintenanceSearchTerm}
                      onChange={(e) => setMaintenanceSearchTerm(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        borderRadius: '0.25rem',
                        border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                        outline: 'none',
                        color: palette.text,
                        '&:focus': {
                          borderColor: palette.primary,
                          boxShadow: `0 0 0 1px ${palette.primary}30`
                        }
                      }}
                    />
                  </div>
                )}
                
                {/* In modalità modifica mostra un avviso che non si può cambiare la manutenzione */}
                {jobId && formData.maintenance && (
                  <div style={{ 
                    backgroundColor: palette.warningBg || '#fff8e6', 
                    borderColor: palette.warningBorder || '#ffeeba',
                    borderWidth: '1px', 
                    borderStyle: 'solid',
                    padding: '0.75rem', 
                    borderRadius: '0.375rem', 
                    marginBottom: '1rem' 
                  }}>
                    <p style={{ color: palette.warningText || '#856404' }} className="flex items-center">
                      <DynamicIcon iconClass="fa-solid fa-info-circle mr-2" />
                      La manutenzione associata non può essere modificata dopo la creazione della commessa.
                    </p>
                  </div>
                )}
                
                {/* Debug Info per aiutare con il troubleshooting */}
                {jobId && (
                  <div style={{ 
                    backgroundColor: '#f0f9ff', 
                    border: '1px solid #bae6fd', 
                    padding: '0.75rem',
                    borderRadius: '0.375rem',
                    marginBottom: '1rem',
                    fontSize: '0.875rem'
                  }}>
                    <p><strong>Debug Manutenzione:</strong></p>
                    <p>ID Manutenzione: {formData.maintenanceId || formData.maintenance?._id || 'Non impostato'}</p>
                    <p>Dati Manutenzione Caricati: {formData.maintenance ? 'Sì' : 'No'}</p>
                    <p>Cliente: {formData.maintenance?.customerLabel || 'Non disponibile'}</p>
                    <p>Impianto: {formData.maintenance?.systemLabel || 'Non disponibile'}</p>
                    <p>Azioni: {formData.maintenance?.actions?.length || 0}</p>
                    <p>Azioni selezionate: {formData.selectedMaintenanceActions?.length || 0}</p>
                  </div>
                )}
                
                {/* Mostra la lista SOLO se non è stata selezionata una manutenzione E non è in modalità modifica */}
                {!formData.maintenance && !jobId ? (
                  maintenancesLoading ? (
                    <div className="py-4 text-center" style={{ color: palette.neutralText || '#718096' }}>
                      <i className="fa-solid fa-spinner fa-spin me-2" style={{ color: palette.primary }}></i>
                      Caricamento manutenzioni...
                    </div>
                  ) : maintenances.length > 0 ? (
                    <div style={{ 
                      border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                      borderRadius: '0.5rem',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {maintenances.map(maintenance => (
                        <div 
                          key={maintenance._id}
                          style={{
                            padding: '0.75rem',
                            borderBottom: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: palette.neutralLightest || '#f9fafb'
                            }
                          }}
                          onClick={() => {
                            // Log completo della manutenzione selezionata
                            console.log('MANUTENZIONE SELEZIONATA COMPLETA:', maintenance);
                            
                            // Aggiorna la manutenzione selezionata
                            updateFormData('maintenance', maintenance);
                            
                            // Log esplicito dei campi della manutenzione rilevanti
                            console.log('CAMPI MANUTENZIONE RILEVANTI:', {
                              customerId: maintenance.customerId,
                              customerLabel: maintenance.customerLabel,
                              systemId: maintenance.systemId,
                              systemLabel: maintenance.systemLabel,
                              systemAddressId: maintenance.systemAddressId,
                              systemAddressLabel: maintenance.systemAddressLabel
                            });
                            
                            // Copia automaticamente i dati dal cliente della manutenzione
                            if (maintenance.customer && typeof maintenance.customer === 'object') {
                              const customerData = {
                                _id: maintenance.customer._id,
                                id: maintenance.customer._id,
                                label: maintenance.customer.label || `${maintenance.customer.name || ''} ${maintenance.customer.surname || ''}`.trim(),
                                value: maintenance.customer._id
                              };
                              updateFormData('customer', customerData);
                              console.log('Cliente aggiornato da oggetto customer:', customerData);
                            } else {
                              // Se il cliente non è un oggetto nidificato, lo creiamo dai campi diretti
                              if (maintenance.customerId && maintenance.customerLabel) {
                                const customerData = {
                                  _id: maintenance.customerId,
                                  id: maintenance.customerId,
                                  label: maintenance.customerLabel,
                                  value: maintenance.customerId
                                };
                                updateFormData('customer', customerData);
                                console.log('Cliente aggiornato da campi diretti:', customerData);
                              }
                            }
                            
                            // Copia automaticamente i dati dall'impianto della manutenzione
                            if (maintenance.system && typeof maintenance.system === 'object') {
                              const systemData = {
                                _id: maintenance.system._id,
                                id: maintenance.system._id,
                                title: maintenance.system.title || maintenance.system.name,
                                label: maintenance.system.title || maintenance.system.name,
                                value: maintenance.system._id
                              };
                              updateFormData('system', systemData);
                              console.log('Impianto aggiornato da oggetto system:', systemData);
                            } else {
                              // Se l'impianto non è un oggetto nidificato, lo creiamo dai campi diretti
                              if (maintenance.systemId && maintenance.systemLabel) {
                                const systemData = {
                                  _id: maintenance.systemId,
                                  id: maintenance.systemId,
                                  title: maintenance.systemLabel,
                                  label: maintenance.systemLabel,
                                  value: maintenance.systemId
                                };
                                updateFormData('system', systemData);
                                console.log('Impianto aggiornato da campi diretti:', systemData);
                              }
                            }
                            
                            // Copia automaticamente i dati dal tipo impianto
                            if (maintenance.systemTypes && maintenance.systemTypes.length > 0) {
                              console.log('Tipi impianto trovati nella manutenzione:', maintenance.systemTypes);
                              
                              const mappedSystemTypes = maintenance.systemTypes.map(type => ({
                                // Garantiamo la presenza di typeId usando tutti i possibili campi
                                typeId: type.systemTypeId || type.typeId || type._id || '',
                                // Garantiamo la presenza di typeLabel usando tutti i possibili campi
                                typeLabel: type.systemTypeLabel || type.typeLabel || type.label || type.name || '',
                                // Aggiungi date di garanzia se disponibili
                                testedDate: type.testedDate || null,
                                expirationWarrantyDate: type.expirationWarrantyDate || null,
                                // Aggiungi eventuali note se disponibili
                                notes: type.notes || ''
                              }));
                              
                              updateFormData('systemTypes', mappedSystemTypes);
                              console.log('Tipi impianto mappati per formData:', mappedSystemTypes);
                            } else {
                              console.warn('Nessun tipo di impianto trovato nella manutenzione selezionata');
                            }
                            
                            console.log('Dati copiati dalla manutenzione:', maintenance);
                          }}
                        >
                          <div className="space-y-2">
                            {/* Prima riga - main info */}
                            <div className="flex justify-between items-start">
                              <div className="space-y-1">
                                <span style={{ fontWeight: '600', color: palette.primary }}>{maintenance.code || maintenance._id.substring(0, 8)}</span>
                                <div className="text-sm">
                                  <span className="font-medium">Cliente:</span> {maintenance.customerLabel || maintenance.customer?.name || 'N/D'}
                                </div>
                                <div className="text-sm">
                                  <span className="font-medium">Impianto:</span> {maintenance.systemLabel || maintenance.system?.title || maintenance.system?.name || 'N/D'}
                                </div>
                                {(maintenance.systemAddress || maintenance.system?.address) && (
                                  <div className="text-sm">
                                    <span className="font-medium">Indirizzo:</span> {maintenance.systemAddress || maintenance.system?.address || 'N/D'}
                                  </div>
                                )}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: palette.neutralText || '#718096' }}>
                                Creata: {new Date(maintenance.creationDate).toLocaleDateString()}
                              </div>
                            </div>
                            
                            {/* Seconda riga - azioni */}
                            {maintenance.actions && maintenance.actions.length > 0 && (
                              <div className="border-t pt-2 mt-2">
                                <div style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', color: palette.neutralText || '#718096' }}>Azioni pianificate:</div>
                                <div className="grid grid-cols-1 gap-1">
                                  {maintenance.actions.map((action, idx) => (
                                    <div key={idx} style={{ 
                                      backgroundColor: palette.neutralLightest || '#f9fafb',
                                      padding: '0.375rem',
                                      borderRadius: '0.25rem',
                                      fontSize: '0.875rem',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}>
                                      <div className="flex-1">
                                        <span className="font-medium">{action.title}</span>
                                        {action.notes && (
                                          <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem', color: palette.neutralText || '#718096' }}>{action.notes}</span>
                                        )}
                                      </div>
                                      <div className="text-xs whitespace-nowrap">
                                        <span style={{
                                          padding: '0 0.375rem',
                                          backgroundColor: palette.primaryBg || palette.primary + '15',
                                          color: palette.primary,
                                          borderRadius: '0.25rem',
                                          marginRight: '0.25rem'
                                        }}>
                                          {action.month}/{action.year}
                                        </span>
                                        {action.statusLabel && (
                                          <span style={{
                                            padding: '0 0.375rem',
                                            backgroundColor: palette.neutralLightest || '#f1f5f9',
                                            color: palette.text,
                                            borderRadius: '0.25rem'
                                          }}>
                                            {action.statusLabel}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '1rem', 
                      textAlign: 'center', 
                      border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                      borderRadius: '0.375rem',
                      color: palette.neutralText || '#718096'
                    }}>
                      {maintenanceSearchTerm.length > 0 ? 
                        "Nessuna manutenzione trovata" : 
                        "Nessuna manutenzione disponibile"}
                    </div>
                  )
                ) : (
                  /* Box con dettagli manutenzione selezionata e pulsante X per rimuovere la selezione */
                  <div style={{ 
                    padding: '1rem',
                    backgroundColor: palette.primaryBg || palette.primary + '15',
                    border: `1px solid ${palette.primary}40`, 
                    borderRadius: '0.5rem' 
                  }}>
                    <div className="flex justify-between items-start">
                      <h4 style={{ fontWeight: '500', color: palette.primary, marginBottom: '0.5rem' }}>Manutenzione selezionata</h4>
                      <button 
                        style={{ 
                          color: palette.neutralText || '#718096',
                          transition: 'color 0.2s ease',
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: '0',
                          '&:hover': {
                            color: palette.danger || '#e53e3e'
                          }
                        }}
                        onClick={() => {
                          // Reset di tutti i dati correlati alla manutenzione
                          updateFormData('maintenance', null);
                          updateFormData('customer', null);
                          updateFormData('system', null);
                          updateFormData('systemTypes', []);
                          
                          console.log('Dati manutenzione resettati');
                        }}
                        title="Rimuovi selezione"
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                    
                    <div style={{ 
                      backgroundColor: '#ffffff',
                      padding: '1rem',
                      borderRadius: '0.5rem',
                      border: `1px solid ${palette.primary}25`,
                      marginTop: '0.5rem'
                    }}>
                      {/* Intestazione con ID */}
                      <div className="flex justify-between items-start">
                        <span style={{ fontWeight: '700', color: palette.primary, fontSize: '1.125rem' }}>
                          {formData.maintenance && (formData.maintenance.code || (formData.maintenance._id && formData.maintenance._id.substring(0, 8)) || 'ID non disponibile')}
                        </span>
                        <div className="flex flex-col gap-1">
                          <span style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.25rem 0.5rem', 
                            borderRadius: '0.375rem', 
                            backgroundColor: palette.primaryBg || palette.primary + '15',
                            color: palette.primary 
                          }}>
                            Creata: {formData.maintenance && formData.maintenance.creationDate ? new Date(formData.maintenance.creationDate).toLocaleDateString() : 'Data non disponibile'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Informazioni sul cliente */}
                      <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${palette.neutralLighter || '#e2e8f0'}` }}>
                        <h5 style={{ fontWeight: '500', color: palette.text, marginBottom: '0.5rem' }}>Informazioni cliente</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                          <div>
                            <span style={{ fontWeight: '500', fontSize: '0.875rem', color: palette.text }}>Cliente:</span>
                            <span style={{ color: palette.neutralText || '#4a5568', marginLeft: '0.5rem' }}>
                              {formData.maintenance?.customerLabel || 'Informazione non disponibile'}
                            </span>
                          </div>
                          
                          <div>
                            <span style={{ fontWeight: '500', fontSize: '0.875rem', color: palette.text }}>Impianto:</span>
                            <span style={{ color: palette.neutralText || '#4a5568', marginLeft: '0.5rem' }}>
                              {formData.maintenance?.systemLabel || 'Informazione non disponibile'}
                            </span>
                          </div>
                          
                          <div className="col-span-2">
                            <span style={{ fontWeight: '500', fontSize: '0.875rem', color: palette.text }}>Indirizzo:</span>
                            <span style={{ color: palette.neutralText || '#4a5568', marginLeft: '0.5rem' }}>
                              {formData.maintenance?.systemAddressLabel || 'Informazione non disponibile'}
                            </span>
                          </div>
                          
                          {formData.maintenance && formData.maintenance.systemTypes && formData.maintenance.systemTypes.length > 0 && (
                            <div className="col-span-2">
                              <span style={{ fontWeight: '500', fontSize: '0.875rem', color: palette.text }}>Tipi impianto:</span>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {formData.maintenance.systemTypes.map((type, idx) => (
                                  <span key={idx} style={{
                                    backgroundColor: palette.neutralLightest || '#f1f5f9', 
                                    color: palette.neutralText || '#4a5568',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.75rem'
                                  }}>
                                    {type.typeLabel || type.label || 'Tipo ' + (idx + 1)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Note */}
                      {formData.maintenance && formData.maintenance.notes && (
                        <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: `1px solid ${palette.neutralLighter || '#e2e8f0'}` }}>
                          <h5 style={{ fontWeight: '500', color: palette.text, marginBottom: '0.5rem' }}>Note</h5>
                          <p style={{ color: palette.neutralText || '#4a5568', fontSize: '0.875rem' }}>{formData.maintenance.notes}</p>
                        </div>
                      )}
                      
                      {/* Azioni con possibilità di selezione */}
                      {formData.maintenance && formData.maintenance.actions && formData.maintenance.actions.length > 0 && (
                        <div className="mt-4 pt-3 border-t">
                          <div style={{
                            backgroundColor: palette.primaryBg || palette.primary + '15',
                            padding: '1rem',
                            borderRadius: '0.5rem',
                            border: `1px solid ${palette.primary}30`
                          }}>
                            <h5 className="font-medium mb-2 flex justify-between items-center" style={{ color: palette.text }}>
                              <div>
                                Azioni pianificate
                                {!jobId && (
                                  <span style={{ fontSize: '0.875rem', fontWeight: 'normal', color: palette.danger, marginLeft: '0.5rem' }}>
                                    (Seleziona le azioni da associare a questa commessa)
                                  </span>
                                )}
                              </div>
                              {formData.selectedMaintenanceActions?.length > 0 && (
                                <span style={{
                                  fontSize: '0.875rem',
                                  backgroundColor: palette.successBg || '#d1fae5',
                                  color: palette.successText || '#047857',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '9999px'
                                }}>
                                  {formData.selectedMaintenanceActions.length} selezionate
                                </span>
                              )}
                            </h5>
                            <div className="space-y-2 mt-3">
                              {formData.maintenance.actions.map((action, idx) => {
                                // Verifica se questa azione è selezionata
                                // Consideriamo sia _id che id per maggiore compatibilità
                                const actionIdentifier = action._id || action.id;
                                const isSelected = formData.selectedMaintenanceActions?.includes(actionIdentifier);
                                
                                return (
                                  <div 
                                    key={idx} 
                                    style={{
                                      padding: '0.75rem', 
                                      borderRadius: '0.375rem',
                                      border: `1px solid ${isSelected ? palette.primary + '60' : palette.neutralLighter || '#e2e8f0'}`,
                                      backgroundColor: isSelected ? (palette.primaryBg || palette.primary + '15') : 'white',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      cursor: jobId ? 'default' : 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                      // Se è in modalità modifica, non permette di modificare le selezioni
                                      if (jobId) return;
                                      
                                      const currentSelected = [...(formData.selectedMaintenanceActions || [])];
                                      const actionIdentifier = action._id || action.id;
                                      const actionIndex = currentSelected.indexOf(actionIdentifier);
                                      
                                      if (actionIndex >= 0) {
                                        // Rimuovi se già selezionato
                                        currentSelected.splice(actionIndex, 1);
                                      } else {
                                        // Aggiungi se non selezionato
                                        currentSelected.push(actionIdentifier);
                                      }
                                      
                                      updateFormData('selectedMaintenanceActions', currentSelected);
                                      console.log(`Azione ${action.title} ${isSelected ? 'deselezionata' : 'selezionata'}`);
                                    }}
                                  >
                                    <div className="flex items-center">
                                      <input
                                        type="checkbox"
                                        style={{ 
                                          marginRight: '0.75rem', 
                                          height: '1rem', 
                                          width: '1rem',
                                          accentColor: palette.primary
                                        }}
                                        checked={isSelected}
                                        disabled={jobId} // Disabilitato in modalità modifica
                                        onChange={() => {}} // Gestito dal click sul div padre
                                        onClick={e => e.stopPropagation()} // Evita doppio trigger
                                      />
                                      <div>
                                        <span style={{ fontWeight: '500', color: palette.text }}>{action.title}</span>
                                        {action.notes && (
                                          <p style={{ fontSize: '0.75rem', color: palette.neutralText || '#718096', marginTop: '0.25rem' }}>{action.notes}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs whitespace-nowrap">
                                      <span style={{
                                        padding: '0 0.375rem',
                                        backgroundColor: palette.primaryBg || palette.primary + '15',
                                        color: palette.primary,
                                        borderRadius: '0.25rem',
                                        marginRight: '0.25rem'
                                      }}>
                                        {action.month}/{action.year}
                                      </span>
                                      {action.statusLabel && (
                                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                                          {action.statusLabel}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Messaggio se non ci sono azioni selezionate e la commessa è di tipo manutenzione */}
                            {formData.jobType?.maintenance && !formData.selectedMaintenanceActions?.length && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-700">
                                <i className="fas fa-exclamation-triangle mr-1"></i>
                                Seleziona almeno un'azione da associare alla commessa
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4 text-sm">
                      <button 
                        style={{ 
                          color: palette.primary,
                          transition: 'color 0.2s ease',
                          '&:hover': { 
                            color: palette.primaryDarker || palette.primary,
                            textDecoration: 'underline' 
                          }
                        }}
                        onClick={() => {
                          // Reset di tutti i dati correlati alla manutenzione
                          updateFormData('maintenance', null);
                          updateFormData('customer', null);
                          updateFormData('system', null);
                          updateFormData('systemTypes', []);
                          
                          console.log('Dati manutenzione resettati');
                        }}
                      >
                        <i className="fa-solid fa-arrow-left mr-1"></i> Torna alla lista
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {determineJobTypeFlag(formData.jobType, 'assistance') && (
              <div className="space-y-4">
                <h3 className="font-semibold mb-2" style={{ color: palette.text }}>Seleziona richiesta di assistenza</h3>
                
                {/* In modalità edit, mostriamo un messaggio che impedisce la modifica dell'assistenza */}
                {jobId ? (
                  <div className="mb-4">
                    {formData.assistanceRequest && (
                      <div style={{
                        marginBottom: '0.75rem',
                        backgroundColor: palette.warningBg || '#fff8e6',
                        borderColor: palette.warningBorder || '#ffeeba',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        padding: '0.75rem',
                        borderRadius: '0.375rem'
                      }}>
                        <p style={{ color: palette.warningText || '#856404' }} className="flex items-center">
                          <DynamicIcon iconClass="fa-solid fa-info-circle mr-2" />
                          La richiesta di assistenza non può essere modificata una volta creata la commessa.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-3">
                    <input
                      type="text"
                      className="w-full p-2 border rounded mb-2"
                      placeholder="Cerca richiesta di assistenza (almeno 3 caratteri)..."
                      value={assistanceSearchTerm}
                      onChange={(e) => setAssistanceSearchTerm(e.target.value)}
                    />
                  </div>
                )}
                
                {/* Mostra la lista SOLO se non è stata selezionata un'assistenza e NON siamo in edit mode */}
                {!formData.assistanceRequest && !jobId ? (
                  assistanceRequestsLoading ? (
                    <div className="py-4 text-center" style={{ color: palette.neutralText || '#718096' }}>
                      <i className="fa-solid fa-spinner fa-spin me-2" style={{ color: palette.primary }}></i>
                      Caricamento richieste...
                    </div>
                  ) : assistanceRequests.length > 0 ? (
                    <div style={{
                      border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                      borderRadius: '0.5rem',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {assistanceRequests.map(request => (
                        <div 
                          key={request._id}
                          style={{
                            padding: '0.75rem',
                            borderBottom: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                            cursor: 'pointer',
                            backgroundColor: formData.assistanceRequest?._id === request._id ? `${palette.primaryBg || palette.primary + '15'}` : 'transparent',
                            transition: 'background-color 0.2s ease',
                            '&:hover': {
                              backgroundColor: palette.neutralLightest || '#f9fafb'
                            }
                          }}
                          onClick={() => {
                            // Aggiorna la richiesta selezionata
                            updateFormData('assistanceRequest', request);
                            
                            // Copia automaticamente i dati dal cliente della richiesta
                            if (request.customerId && request.customerLabel) {
                              const customerData = {
                                _id: request.customerId,
                                id: request.customerId,
                                label: request.customerLabel,
                                value: request.customerId
                              };
                              updateFormData('customer', customerData);
                            }
                            
                            // Copia automaticamente i dati dall'impianto
                            if (request.systemId && request.systemLabel) {
                              const systemData = {
                                _id: request.systemId,
                                id: request.systemId,
                                title: request.systemLabel,
                                label: request.systemLabel,
                                value: request.systemId
                              };
                              updateFormData('system', systemData);
                            }

                            // Non è necessario caricare qui i dettagli della richiesta di assistenza o della manutenzione,
                            // poiché vengono gestiti alla prima chiamata di fetchJobData

                            // Copia automaticamente i dati dal tipo impianto
                            if (request.systemTypeId && request.systemTypeLabel) {
                              console.log('Tipo impianto trovato nella richiesta di assistenza:', {
                                systemTypeId: request.systemTypeId,
                                systemTypeLabel: request.systemTypeLabel,
                                systemTypeExpirationWarrantyDate: request.systemTypeExpirationWarrantyDate
                              });
                              
                              const systemTypeData = {
                                typeId: request.systemTypeId,
                                typeLabel: request.systemTypeLabel,
                                testedDate: null, // Di default null per le richieste di assistenza
                                expirationWarrantyDate: request.systemTypeExpirationWarrantyDate || null,
                                notes: request.systemTypeNotes || ''
                              };
                              
                              updateFormData('systemTypes', [systemTypeData]);
                              console.log('Tipo impianto mappato per assistenza:', systemTypeData);
                            } else {
                              console.warn('Nessun tipo di impianto trovato nella richiesta di assistenza');
                            }
                            
                            // Copia i dati geografici (geoData) per evitare errori di validazione lato server
                            if (request.geoData) {
                              // Se la richiesta ha già dati geografici, li usiamo
                              updateFormData('geoData', request.geoData);
                            } else if (request.systemAddressId && (request.geoData?.coordinates || (request.outboundCoordinateLat && request.outboundCoordinateLng))) {
                              // Se ha coordinate nel formato richiesto o separato, creiamo l'oggetto geoData
                              const coordinates = request.geoData?.coordinates || [
                                parseFloat(request.outboundCoordinateLng) || 0,
                                parseFloat(request.outboundCoordinateLat) || 0
                              ];
                              
                              const geoData = {
                                type: "Point",
                                coordinates: coordinates
                              };
                              
                              updateFormData('geoData', geoData);
                            } else {
                              // Creiamo un geoData di default usando le coordinate della sede centrale dall'account
                              // Verifichiamo se abbiamo headquarterCoordinates nelle impostazioni dell'account
                              if (accountSettings.headquarterCoordinates && 
                                  Array.isArray(accountSettings.headquarterCoordinates) && 
                                  accountSettings.headquarterCoordinates.length === 2) {
                                // Usa le coordinate della sede centrale dall'account settings
                                const geoData = {
                                  type: "Point",
                                  coordinates: accountSettings.headquarterCoordinates
                                };
                                updateFormData('geoData', geoData);
                                console.log('Creato geoData usando le coordinate della sede centrale');
                              } else {
                                // Fallback in caso di mancanza di configurazione delle coordinate della sede
                                console.warn('Coordinate della sede centrale non configurate nelle impostazioni account');
                                // Qui potremmo lasciare geoData vuoto e lasciare che il server gestisca l'errore,
                                // oppure utilizzare un valore di default molto generico
                                const geoData = {
                                  type: "Point",
                                  coordinates: [0, 0] // Coordinata neutra solo per superare la validazione
                                };
                                updateFormData('geoData', geoData);
                              }
                            }
                            
                            console.log('Dati copiati dalla richiesta di assistenza:', request);
                          }
                          }
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span style={{ fontWeight: '500', color: palette.primary }}>{request.code || `#${request._id}`}</span>
                              <div className="text-sm">
                                <span className="font-medium">Cliente:</span> {request.customerLabel}
                              </div>
                              {request.systemLabel && (
                                <div className="text-sm">
                                  <span className="font-medium">Impianto:</span> {request.systemLabel}
                                </div>
                              )}
                            </div>
                            <div style={{ fontSize: '0.75rem' }}>
                              <div style={{ color: palette.neutralText || '#718096' }}>
                                Creata: {new Date(request.creationDate).toLocaleDateString()}
                              </div>
                              {request.expirationDate && (
                                <div style={{ 
                                  color: new Date(request.expirationDate) < new Date() ? (palette.danger || '#e53e3e') : palette.primary
                                }}>
                                  Scadenza: {new Date(request.expirationDate).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{
                      padding: '1rem',
                      textAlign: 'center',
                      border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                      borderRadius: '0.375rem',
                      color: palette.neutralText || '#718096'
                    }}>
                      {assistanceSearchTerm.length > 0 ? 
                        "Nessuna richiesta di assistenza trovata" : 
                        "Nessuna richiesta di assistenza disponibile"}
                    </div>
                  )
                ) : (
                  /* Box con dettagli assistenza selezionata */
                  <div style={{
                    padding: '1rem',
                    backgroundColor: palette.primaryBg || palette.primary + '15',
                    border: `1px solid ${palette.primary}40`,
                    borderRadius: '0.5rem'
                  }}>
                    <div className="flex justify-between items-start">
                      <h4 style={{ fontWeight: '500', color: palette.primary, marginBottom: '0.5rem' }}>Richiesta di assistenza selezionata</h4>
                      {/* Pulsante rimozione visibile solo in modalità creazione */}
                      {!jobId && (
                        <button 
                          style={{
                            color: palette.neutralText || '#718096',
                            transition: 'color 0.2s ease',
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: '0'
                          }}
                          onClick={() => {
                            // Reset di tutti i dati correlati alla richiesta di assistenza
                            updateFormData('assistanceRequest', null);
                            updateFormData('customer', null);
                            updateFormData('system', null);
                            updateFormData('systemTypes', []);
                            
                            console.log('Dati richiesta assistenza resettati');
                          }}
                          title="Rimuovi selezione"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      )}
                    </div>
                    <AssistanceDetails 
                      assistance={formData.assistanceRequest}
                      onRemove={() => {
                        updateFormData('assistanceRequest', null);
                        updateFormData('customer', null);
                        updateFormData('system', null);
                        updateFormData('systemTypes', []);
                        console.log('Dati richiesta assistenza resettati');
                      }}
                      readOnly={!!jobId}
                    />
                  </div>
                )}
              </div>
            )}
            
            {formData.jobType?.generic && (
              <div className="mt-4 space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">Seleziona cliente</h3>
                  {jobId ? (
                    /* In modalità edit, il cliente non dev'essere modificabile */
                    <div className="mb-4">
                      <div style={{
                        border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                        padding: '0.75rem',
                        borderRadius: '0.375rem',
                        backgroundColor: palette.neutralLightest || '#f9fafb'
                      }}>
                        <div className="flex justify-between items-center">
                          <div>
                            <span style={{ color: palette.primary, fontWeight: '500', fontSize: '1.125rem' }}>{formData.customer?.label || 'Cliente non specificato'}</span>
                            {formData.customer?.value && (
                              <p style={{ fontSize: '0.75rem', color: palette.neutralText || '#718096', marginTop: '0.25rem' }}>ID: {formData.customer.value}</p>
                            )}
                          </div>
                          <span style={{
                            backgroundColor: palette.primaryBg || palette.primary + '15',
                            color: palette.primary,
                            padding: '0.25rem 0.75rem',
                            borderRadius: '9999px',
                            fontSize: '0.875rem'
                          }}>Cliente selezionato</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1 flex items-center">
                        <DynamicIcon iconClass="fa-solid fa-info-circle mr-1" />
                        Il cliente non può essere modificato in fase di modifica della commessa
                      </p>
                    </div>
                  ) : (
                    /* In modalità creazione, il cliente è selezionabile */
                    <div className="mb-4">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <CustomerSelect
                            value={formData.customer}
                            onChange={(selected) => {
                              updateFormData('customer', selected);
                            }}
                            excludeProspects={true}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setWasCreatingCustomer(true);
                            setWasCreatingSystem(false); // Reset flag impianto
                            dispatch(setModal({ mode: 'add' }));
                          }}
                          className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center transition-colors"
                          style={{
                            backgroundColor: palette.primary,
                            color: palette.textOnPrimary || '#ffffff'
                          }}
                          title="Crea nuovo cliente"
                        >
                          <i className="fa-solid fa-plus text-lg"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Mostra lista impianti se il cliente è selezionato */}
                {formData.customer && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold">Seleziona impianto</h3>
                      <button
                        type="button"
                        onClick={() => {
                          setWasCreatingSystem(true);
                          setWasCreatingCustomer(false); // Reset flag cliente
                          // Passa i dati del cliente selezionato alla modale
                          const customerData = {
                            id: formData.customer.id,
                            label: formData.customer.label,
                            value: formData.customer.value
                          };
                          console.log('Apertura modale creazione impianto con cliente:', customerData);
                          dispatch(setModal({ 
                            mode: 'add',
                            data: {
                              preselectedCustomer: customerData
                            }
                          }));
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-md transition-colors"
                        style={{
                          backgroundColor: palette.primary,
                          color: palette.textOnPrimary || '#ffffff'
                        }}
                        title="Crea nuovo impianto"
                      >
                        <i className="fa-solid fa-plus text-sm"></i>
                      </button>
                    </div>
                    {customerSystemsLoading ? (
                      <div className="py-4 text-center text-gray-500">
                        <svg className="animate-spin h-6 w-6 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Caricamento impianti...
                      </div>
                    ) : customerSystems.length === 0 ? (
                      <div className="text-center py-3 bg-gray-50 rounded-lg text-gray-500">
                        Nessun impianto trovato per questo cliente
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {customerSystems.map(system => (
                          <div 
                            key={system._id}
                            data-system-id={system._id}
                            className="border rounded-lg p-4 cursor-pointer transition-all"
                            style={{
                              backgroundColor: formData.system?._id === system._id ? (palette.primaryBg || palette.primary + '15') : 'transparent',
                              borderColor: formData.system?._id === system._id ? palette.primary : palette.neutralLighter || '#e2e8f0',
                              '&:hover': {
                                backgroundColor: formData.system?._id !== system._id ? (palette.neutralLightest || '#f9fafb') : undefined
                              }
                            }}
                            onClick={() => {
                              // Reset dei tipi selezionati quando cambia l'impianto
                              updateFormData('system', system);
                              updateFormData('systemTypes', []);
                            }}
                          >
                            <div className="font-medium">{system.title}</div>
                            <div className="text-sm text-gray-600">{system.customerAddressLabel}</div>
                            
                            {/* Informazioni di distanza e tempo */}
                            {system.geoData && (system.geoData.distanceKm || system.geoData.distanceTime) && (
                              <div className="mt-2 flex items-center space-x-3 text-xs">
                                {system.geoData.distanceKm > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <i className="fa-solid fa-route mr-1" style={{ color: palette.primary }}></i>
                                    {formatDistance(system.geoData.distanceKm)}
                                  </span>
                                )}
                                {system.geoData.distanceTime > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                    <i className="fa-solid fa-clock mr-1" style={{ color: palette.primary }}></i>
                                    {formatDurationMinutes(system.geoData.distanceTime)}
                                  </span>
                                )}
                              </div>
                            )}
                            
                            {/* Lista tipi */}
                            {system.typesList && system.typesList.length > 0 && (
                              <div className="mt-3 border-t pt-2">
                                <div className="text-sm font-medium mb-1">Tipi di impianto:</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {system.typesList.map(type => {
                                    const isSelected = formData.system?._id === system._id && 
                                      formData.systemTypes.some(t => t.typeId === type.typeId);
                                    
                                    // Calcola se in garanzia
                                    const hasWarranty = type.expirationWarrantyDate;
                                    const warrantyDate = hasWarranty ? new Date(type.expirationWarrantyDate) : null;
                                    const isWarrantyExpired = hasWarranty && warrantyDate < new Date();
                                    
                                    return (
                                      <div 
                                        key={type.typeId}
                                        className={`
                                          px-3 py-2 text-sm rounded-md flex items-center justify-between
                                          ${isSelected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                                          ${formData.system?._id === system._id ? 'cursor-pointer hover:bg-opacity-80' : 'opacity-60 cursor-not-allowed'}
                                        `}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (formData.system?._id !== system._id) return;
                                          
                                          // Toggle selezione tipo
                                          const currentTypes = [...(formData.systemTypes || [])];
                                          const typeIndex = currentTypes.findIndex(t => t.typeId === type.typeId);
                                          
                                          if (typeIndex >= 0) {
                                            // Rimuovi se già selezionato
                                            currentTypes.splice(typeIndex, 1);
                                          } else {
                                            // Aggiungi se non selezionato
                                            currentTypes.push(type);
                                          }
                                          
                                          updateFormData('systemTypes', currentTypes);
                                        }}
                                      >
                                        <span>{type.typeLabel}</span>
                                        {hasWarranty && (
                                          <span style={{
                                            fontSize: '0.75rem',
                                            padding: '0.25rem 0.5rem',
                                            borderRadius: '0.25rem',
                                            backgroundColor: isWarrantyExpired ? (palette.dangerBg || palette.danger + '15') : (palette.primaryBg || palette.primary + '15'),
                                            color: isWarrantyExpired ? (palette.danger || '#e53e3e') : palette.primary
                                          }}>
                                            {isWarrantyExpired ? 'Garanzia scaduta' : 'In garanzia'}: {warrantyDate.toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {formData.jobType && !formData.jobType.assistance && !formData.jobType.generic && !determineJobTypeFlag(formData.jobType, 'maintenance') && (
              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="font-semibold mb-2">Seleziona cliente</h3>
                  <CustomerSelect
                    value={formData.customer}
                    onChange={(selected) => {
                      updateFormData('customer', selected);
                    }}
                    excludeProspects={true}
                  />
                </div>
                
                {formData.customer && (
                  <div>
                    <h3 className="font-semibold mb-2">Seleziona impianto</h3>
                    {customerSystemsLoading ? (
                      <div className="text-center py-2">Caricamento impianti...</div>
                    ) : (
                      <select 
                        className="w-full p-2 border rounded"
                        value={formData.system?.id || ''}
                        onChange={(e) => {
                          const selectedId = parseInt(e.target.value, 10);
                          const selected = customerSystems.find(s => s.id === selectedId);
                          updateFormData('system', selected);
                          
                          // Se l'impianto ha tipi di impianto nella sua typesList, li copiamo in systemTypes
                          if (selected && selected.typesList && Array.isArray(selected.typesList) && selected.typesList.length > 0) {
                            console.log('Impianto selezionato ha typesList:', selected.typesList);
                            
                            // Mappiamo i tipi dall'impianto nel formato richiesto per systemTypes
                            const mappedTypes = selected.typesList.map(type => ({
                              typeId: type.typeId || '',
                              typeLabel: type.typeLabel || '',
                              testedDate: type.testedDate || null,
                              expirationWarrantyDate: type.expirationWarrantyDate || null,
                              notes: type.notes || ''
                            }));
                            
                            console.log('Tipi impianto mappati da typesList:', mappedTypes);
                            updateFormData('systemTypes', mappedTypes);
                          } else {
                            // Se non ci sono tipi di impianto, resettiamo
                            console.log('Nessun tipo impianto trovato nell\'impianto selezionato');
                            updateFormData('systemTypes', []);
                          }
                        }}
                      >
                        <option value="">Seleziona impianto...</option>
                        {customerSystems.map(system => (
                          <option key={system.id} value={system.id}>{system.name}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                
                {formData.system && (
                  <div>
                    <h3 className="font-semibold mb-2">Seleziona tipi impianto</h3>
                    <div className="space-y-2">
                      {formData.system.typesList && formData.system.typesList.length > 0 ? (
                        formData.system.typesList.map(type => {
                          const isSelected = formData.systemTypes.some(t => t.typeId === type.typeId);
                          // Calcola se in garanzia
                          const hasWarranty = type.expirationWarrantyDate;
                          const warrantyDate = hasWarranty ? new Date(type.expirationWarrantyDate) : null;
                          const isWarrantyExpired = hasWarranty && warrantyDate < new Date();
                            
                          return (
                            <div 
                              key={type.typeId}
                              onClick={() => {
                                // Toggle selezione tipo
                                const currentTypes = [...(formData.systemTypes || [])];
                                const typeIndex = currentTypes.findIndex(t => t.typeId === type.typeId);
                                
                                if (typeIndex >= 0) {
                                  // Rimuovi se già selezionato
                                  currentTypes.splice(typeIndex, 1);
                                } else {
                                  // Aggiungi se non selezionato
                                  currentTypes.push(type);
                                }
                                
                                updateFormData('systemTypes', currentTypes);
                              }}
                              className={`
                                p-3 rounded border cursor-pointer flex justify-between items-center
                                ${isSelected ? 'bg-green-50 border-green-500' : 'hover:bg-gray-50'}
                              `}
                            >
                              <span className="font-medium">{type.typeLabel}</span>
                              {hasWarranty && (
                                <span className={`text-xs px-2 py-1 rounded ${isWarrantyExpired ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                  {isWarrantyExpired ? 'Garanzia scaduta' : 'In garanzia'}: {warrantyDate.toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-3 bg-gray-50 rounded-lg text-gray-500">
                          Nessun tipo impianto disponibile
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        
      case 2: // Step 3: Descrizione
        return (
          <div>
            <h2 className="text-xl font-bold mb-4">Descrizione attività</h2>
            <textarea
              className="w-full border rounded-lg p-3 h-40"
              value={formData.description}
              onChange={(e) => updateFormData('description', e.target.value)}
              placeholder="Inserisci una descrizione dettagliata dell'attività..."
            />
          </div>
        );
        
      case 3: // Step 4: Dati lavorazione
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold mb-4">Dati di lavorazione</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Numero tecnici */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Numero tecnici
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full p-2 border rounded"
                  value={formData.processingData.techniciansCount}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10) || 1;
                    updateFormData('processingData', {
                      ...formData.processingData,
                      techniciansCount: Math.max(1, value)
                    });
                  }}
                />
              </div>
              
              {/* Ore previste */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ore previste
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.5"
                  className="w-full p-2 border rounded"
                  value={formData.processingData.estimatedHours}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 1;
                    updateFormData('processingData', {
                      ...formData.processingData,
                      estimatedHours: Math.max(0.5, value)
                    });
                  }}
                />
              </div>
            </div>
            
            {/* Data scadenza */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data scadenza
              </label>
              <input
                type="date"
                className="w-full p-2 border rounded"
                value={formData.processingData.dueDate || ''}
                min={new Date().toISOString().split('T')[0]} // Data minima è oggi
                onChange={(e) => {
                  updateFormData('processingData', {
                    ...formData.processingData,
                    dueDate: e.target.value
                  });
                }}
              />
            </div>
            
            {/* Files allegati */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Files allegati
              </label>
              <div className="border rounded-lg p-4 bg-gray-50">
                <input
                  type="file"
                  multiple
                  className="w-full mb-3"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
                    
                    // Crea un array di oggetti per gli allegati (nome file, tipo, ecc)
                    const newAttachments = files.map(file => ({
                      name: file.name,
                      type: file.type,
                      size: file.size,
                      file: file // Conserva il file stesso per l'invio successivo
                    }));
                    
                    updateFormData('attachments', [...formData.attachments, ...newAttachments]);
                  }}
                />
                
                {/* Gallery di anteprima file */}
                {formData.attachments.length > 0 && (
                  <div className="mt-3">
                    <p className="font-medium mb-3 text-sm" style={{ color: palette.primary }}>
                      <i className="fa-solid fa-paperclip me-1"></i>
                      Files Allegati ({formData.attachments.length})
                    </p>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {formData.attachments.map((file, index) => (
                        <FilePreview
                          key={index}
                          file={file}
                          palette={palette}
                          onDelete={() => {
                            const newAttachments = [...formData.attachments];
                            newAttachments.splice(index, 1);
                            updateFormData('attachments', newAttachments);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Preview commessa
  const renderPreview = () => {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <h2 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', borderBottom: `1px solid ${palette.neutralLighter || '#e2e8f0'}`, paddingBottom: '0.5rem', color: palette.primary }}>Anteprima Commessa</h2>
        
        {formData.jobType ? (
          <div className="space-y-4">
            <div>
              <h3 style={{ fontWeight: '500', color: palette.text }}>Tipo commessa</h3>
              <p>{formData.jobType.title}</p>
            </div>
            
            {formData.assistanceRequest && (
              <div>
                <h3 style={{ fontWeight: '500', color: palette.text }}>Richiesta assistenza</h3>
                <div style={{
                  backgroundColor: palette.primaryBg || palette.primary + '15',
                  border: `1px solid ${palette.primary}25`,
                  borderRadius: '0.375rem',
                  padding: '0.75rem'
                }}>
                  <p style={{ fontWeight: '500', color: palette.primary }}>{formData.assistanceRequest.code || `#${formData.assistanceRequest._id}`}</p>
                  <p style={{ fontSize: '0.875rem' }}><span style={{ fontWeight: '500' }}>Cliente:</span> {formData.assistanceRequest.customerLabel}</p>
                  {formData.assistanceRequest.systemLabel && (
                    <p style={{ fontSize: '0.875rem' }}><span style={{ fontWeight: '500' }}>Impianto:</span> {formData.assistanceRequest.systemLabel}</p>
                  )}
                  {formData.assistanceRequest.assistanceTypePriorityLabel && (
                    <div className="mt-1">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${formData.assistanceRequest.assistanceTypePriorityValue === 'high' ? 'bg-red-100 text-red-800' : formData.assistanceRequest.assistanceTypePriorityValue === 'medium' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                        {formData.assistanceRequest.assistanceTypePriorityLabel}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {formData.customer && (
              <div>
                <h3 className="font-medium text-gray-700">Cliente</h3>
                <p>{formData.customer.label}</p>
              </div>
            )}
            
            {formData.system && (
              <div>
                <h3 className="font-medium text-gray-700">Impianto</h3>
                <p>{formData.system.title}</p>
                {formData.system.customerAddressLabel && (
                  <p className="text-sm text-gray-500">{formData.system.customerAddressLabel}</p>
                )}
              </div>
            )}
            
            {/* Sezione manutenzione per commesse di tipo manutenzione */}
            {formData.jobType?.maintenance && formData.maintenance && (
              <div>
                <h3 className="font-medium text-gray-700">Manutenzione</h3>
                <div className="mt-1 space-y-1">
                  <div className="text-sm">
                    <span className="font-medium">Codice:</span> {formData.maintenance.code || '-'}
                  </div>
                  {formData.maintenance.title && (
                    <div className="text-sm">
                      <span className="font-medium">Nome:</span> {formData.maintenance.title}
                    </div>
                  )}
                  <div className="text-sm">
                    <span className="font-medium">Cliente:</span> {formData.maintenance.customerLabel || '-'}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium">Impianto:</span> {formData.maintenance.systemLabel || '-'}
                  </div>
                  
                  {/* Azioni di manutenzione selezionate */}
                  {formData.selectedMaintenanceActions && formData.selectedMaintenanceActions.length > 0 && formData.maintenance?.actions && (
                    <div className="mt-2">
                      <div style={{ fontSize: '0.875rem', fontWeight: '500', color: palette.primary }}>
                        Azioni selezionate: {formData.selectedMaintenanceActions.length}
                      </div>
                      <ul style={{
                        marginTop: '0.25rem',
                        fontSize: '0.75rem',
                        backgroundColor: palette.primaryBg || palette.primary + '15',
                        border: `1px solid ${palette.primary}25`,
                        borderRadius: '0.25rem',
                        padding: '0.5rem'
                      }}>
                        {formData.maintenance.actions
                          .filter(action => formData.selectedMaintenanceActions.includes(action._id || action.id))
                          .map(action => (
                            <li key={action._id} className="mb-1 pb-1 border-b border-blue-100 last:border-0 last:mb-0 last:pb-0">
                              <div className="font-medium">{action.title}</div>
                              {action.statusLabel && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-blue-100">
                                  {action.statusLabel}
                                </span>
                              )}
                              {action.month && action.year && (
                                <span style={{ fontSize: '0.75rem', color: palette.primary, marginLeft: '0.25rem' }}>
                                  {action.month}/{action.year}
                                </span>
                              )}
                            </li>
                          ))
                        }
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Sezione tipi impianto selezionati per altri tipi di commesse */}
            {!formData.jobType?.maintenance && formData.systemTypes && formData.systemTypes.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700">Tipi impianto selezionati</h3>
                <div className="flex flex-wrap gap-2 mt-1">
                  {formData.systemTypes.map(type => (
                    <span key={type.typeId} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                      {type.typeLabel}
                      {type.expirationWarrantyDate && (
                        <span className="ml-1 text-xs opacity-80">
                          {new Date(type.expirationWarrantyDate) < new Date() ? '(Garanzia scaduta)' : '(In garanzia)'}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {formData.description && (
              <div>
                <h3 className="font-medium text-gray-700">Descrizione</h3>
                <p className="text-sm">{formData.description}</p>
              </div>
            )}
            
            {/* Dati di lavorazione nella preview */}
            {formData.processingData.techniciansCount > 0 && (
              <div>
                <h3 className="font-medium text-gray-700">Dati di lavorazione</h3>
                <ul className="text-sm space-y-1">
                  <li><span className="font-medium">Tecnici:</span> {formData.processingData.techniciansCount}</li>
                  <li><span className="font-medium">Ore previste:</span> {formData.processingData.estimatedHours}</li>
                  {formData.processingData.dueDate && (
                    <li>
                      <span className="font-medium">Scadenza:</span> {
                        new Date(formData.processingData.dueDate).toLocaleDateString()
                      }
                    </li>
                  )}
                </ul>
              </div>
            )}
            
            {/* Allegati nella preview */}
            {formData.attachments && formData.attachments.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700">Allegati</h3>
                <ul className="text-sm list-disc list-inside">
                  {formData.attachments.map((file, index) => (
                    <li key={index} className="truncate">{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500 italic">Seleziona un tipo di commessa per iniziare</p>
        )}
      </div>
    );
  };
  
  return (
    <div className={`${isModal ? 'h-full overflow-y-auto' : 'page'}`}>
      {/* Header con titolo e pulsante chiudi per la modale */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          {jobId && (
            <button
              onClick={() => navigate('/jobs')}
              className="mr-3 px-3 py-1.5 rounded flex items-center transition-colors"
              style={{
                backgroundColor: palette.primary,
                color: palette.textOnPrimary || '#ffffff',
                '&:hover': {
                  backgroundColor: palette.primaryDarker || palette.primary
                }
              }}
              title="Torna alla lista commesse"
            >
              <i className="fas fa-arrow-left mr-1"></i>
              <span>Indietro</span>
            </button>
          )}
          <h1 className="text-2xl font-bold">
            {jobId ? 'Modifica Commessa' : 'Nuova Commessa'}
            {jobId && formData.code && (
              <span className="ml-2 font-semibold" style={{ color: palette.primary }}>
                {formData.code}
              </span>
            )}
          </h1>
        </div>
        {isModal && (
          <button 
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
          >
            <i className="fas fa-times text-xl" />
          </button>
        )}
      </div>

      {/* Contenuto del wizard */}
      <div className={`${isModal ? 'px-4' : ''}`}>
        {/* Modale di conferma */}
        <ConfirmModal 
          isOpen={showConfirmModal} 
          onCancel={() => setShowConfirmModal(false)} 
          onConfirm={confirmCreateJob} 
          jobData={formData}
          isLoading={submitting}
          isEditMode={!!jobId}
        />
        
        {/* Stepper con nuovo stile */}
        <div className="mb-8 bg-white rounded-lg shadow-md p-4">
          <div className="bs-stepper-header py-4">
            <div className="flex justify-between items-center gap-1">
              {steps.map((step, index) => (
                <React.Fragment key={index}>
                  <div 
                    className={`step flex flex-col items-center cursor-pointer ${currentStep === index ? 'active' : ''}`}
                    onClick={() => index <= currentStep && setCurrentStep(index)}
                  >
                    <StepIcon 
                      icon={step.icon} 
                      isActive={currentStep === index} 
                      isCompleted={currentStep > index} 
                    />
                    <span 
                      className="mt-2 text-sm font-medium text-center px-1"
                      style={{
                        color: currentStep === index 
                          ? palette.primary 
                          : currentStep > index 
                            ? palette.primary 
                            : '#6B7280'
                      }}
                    >
                      {step.title}
                    </span>
                  </div>
                  
                  {index < steps.length - 1 && (
                    <div className="line flex items-center flex-shrink-0">
                      <div 
                        className="h-0.5 w-6 md:w-8"
                        style={{
                          backgroundColor: currentStep > index ? palette.primary : '#E5E7EB'
                        }}
                      ></div>
                      <i 
                        className="fa-solid fa-chevron-right mx-1 md:mx-2"
                        style={{
                          color: currentStep > index ? palette.primary : '#D1D5DB'
                        }}
                      ></i>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex flex-col md:flex-row gap-6">
          {/* Colonna sinistra - Wizard */}
          <div className="w-full md:w-3/5 bg-white rounded-lg shadow-md p-6">
            {renderStep()}
            
            <div className="flex justify-between mt-8">
              <button 
                onClick={prevStep} 
                disabled={currentStep === 0}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.375rem',
                  border: `1px solid ${palette.neutralLighter || '#e2e8f0'}`,
                  backgroundColor: 'transparent',
                  color: palette.text,
                  opacity: currentStep === 0 ? '0.5' : '1',
                  cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Indietro
              </button>
              
              {currentStep < steps.length - 1 ? (
                <button 
                  onClick={nextStep}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    backgroundColor: palette.primary,
                    color: palette.textOnPrimary || '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: palette.primaryDarker || palette.primary + 'dd'
                    }
                  }}
                >
                  Avanti
                </button>
              ) : (
                <button 
                  onClick={handleCreateJob} 
                  disabled={submitting}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    backgroundColor: palette.success || '#48bb78',
                    color: '#ffffff',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? '0.5' : '1',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{jobId ? 'Aggiornamento' : 'Creazione'} in corso...</span>
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{jobId ? 'Aggiorna commessa' : 'Crea nuova commessa'}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          
          {/* Colonna destra - Preview */}
          <div className="w-full md:w-2/5">
            {renderPreview()}
          </div>
        </div>
      </div>
      
      {/* RightModal per creazione cliente o impianto */}
      {rightModalIsOpen && (rightModalMode === 'add' || rightModalMode === 'add_address') && (
        <RightModal>
          {rightModalMode === 'add' && wasCreatingCustomer && <AddEditCustomer customerId={rightModalCustomerId} />}
          {rightModalMode === 'add' && wasCreatingSystem && (
            <AddEditSystem 
              systemId={null} 
              preselectedCustomer={rightModalData?.preselectedCustomer || null}
            />
          )}
          {rightModalMode === 'add_address' && (
            <AddEditAddress 
              customerId={rightModalData?.customerId}
              customerData={rightModalData?.customerData}
            />
          )}
        </RightModal>
      )}
    </div>
  );
};

export default CreateJob;
